/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ProcessStartedRequest } from "../../continuum/rpc/continuum_rpc";
import { Struct, Value } from "../../google/protobuf/struct";
import { Timestamp } from "../../google/protobuf/timestamp";
import { BuildInfo } from "../../pkg/discovery/discovery";
import { LoginBeginRequest } from "../../pkg/rpc/auth/auth";

export const protobufPackage = "mesh";

/**
 * The coarse mailbox lifecycle. An enum rather than a string: the set is closed and the
 * server resolves it to concrete purge/close timeouts. UNSPECIFIED = the server default.
 */
export enum MailboxLifecycle {
  MAILBOX_LIFECYCLE_UNSPECIFIED = 0,
  SHORT_LIVED_CLI = 1,
  LONG_LIVED_DAEMON = 2,
  UNRECOGNIZED = -1,
}

export function mailboxLifecycleFromJSON(object: any): MailboxLifecycle {
  switch (object) {
    case 0:
    case "MAILBOX_LIFECYCLE_UNSPECIFIED":
      return MailboxLifecycle.MAILBOX_LIFECYCLE_UNSPECIFIED;
    case 1:
    case "SHORT_LIVED_CLI":
      return MailboxLifecycle.SHORT_LIVED_CLI;
    case 2:
    case "LONG_LIVED_DAEMON":
      return MailboxLifecycle.LONG_LIVED_DAEMON;
    case -1:
    case "UNRECOGNIZED":
    default:
      return MailboxLifecycle.UNRECOGNIZED;
  }
}

export function mailboxLifecycleToJSON(object: MailboxLifecycle): string {
  switch (object) {
    case MailboxLifecycle.MAILBOX_LIFECYCLE_UNSPECIFIED:
      return "MAILBOX_LIFECYCLE_UNSPECIFIED";
    case MailboxLifecycle.SHORT_LIVED_CLI:
      return "SHORT_LIVED_CLI";
    case MailboxLifecycle.LONG_LIVED_DAEMON:
      return "LONG_LIVED_DAEMON";
    case MailboxLifecycle.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export enum HelloErrorCode {
  HELLO_ERROR_UNSPECIFIED = 0,
  /** UNAUTHENTICATED - bad/expired token or signature — re-login, do NOT retry */
  UNAUTHENTICATED = 1,
  /** FORBIDDEN - authenticated but wrong role — permanent */
  FORBIDDEN = 2,
  /** MAILBOX_NOT_FOUND - resume target gone — decide deliberately, do NOT auto-mint */
  MAILBOX_NOT_FOUND = 3,
  /** INVALID_REQUEST - malformed hello — permanent */
  INVALID_REQUEST = 4,
  /** UNAVAILABLE - server/database transient — retry with backoff */
  UNAVAILABLE = 5,
  /**
   * NATS_STREAM_NOT_FOUND - The mailbox RECORD exists but its INBOX IS GONE: the ledger reaper deleted the
   * NATS stream after the owning processrun went awol (a closed tab, or a lid shut
   * past the AWOL window). Recover exactly as for MAILBOX_NOT_FOUND — drop the stale
   * keys and mint a fresh session; a resume can never succeed.
   *
   * Named NATS_STREAM_NOT_FOUND, not STREAM_NOT_FOUND: "stream" is overloaded here.
   * The continuum.stream ledger ROW and the NATS JetStream STREAM are different
   * objects with different lifecycles — the row outlives the stream, which is the
   * whole bug — so the code says which one it means.
   * BUG-20260825-rpc-responder-floods-on-reclaimed-mailbox.
   */
  NATS_STREAM_NOT_FOUND = 6,
  UNRECOGNIZED = -1,
}

export function helloErrorCodeFromJSON(object: any): HelloErrorCode {
  switch (object) {
    case 0:
    case "HELLO_ERROR_UNSPECIFIED":
      return HelloErrorCode.HELLO_ERROR_UNSPECIFIED;
    case 1:
    case "UNAUTHENTICATED":
      return HelloErrorCode.UNAUTHENTICATED;
    case 2:
    case "FORBIDDEN":
      return HelloErrorCode.FORBIDDEN;
    case 3:
    case "MAILBOX_NOT_FOUND":
      return HelloErrorCode.MAILBOX_NOT_FOUND;
    case 4:
    case "INVALID_REQUEST":
      return HelloErrorCode.INVALID_REQUEST;
    case 5:
    case "UNAVAILABLE":
      return HelloErrorCode.UNAVAILABLE;
    case 6:
    case "NATS_STREAM_NOT_FOUND":
      return HelloErrorCode.NATS_STREAM_NOT_FOUND;
    case -1:
    case "UNRECOGNIZED":
    default:
      return HelloErrorCode.UNRECOGNIZED;
  }
}

export function helloErrorCodeToJSON(object: HelloErrorCode): string {
  switch (object) {
    case HelloErrorCode.HELLO_ERROR_UNSPECIFIED:
      return "HELLO_ERROR_UNSPECIFIED";
    case HelloErrorCode.UNAUTHENTICATED:
      return "UNAUTHENTICATED";
    case HelloErrorCode.FORBIDDEN:
      return "FORBIDDEN";
    case HelloErrorCode.MAILBOX_NOT_FOUND:
      return "MAILBOX_NOT_FOUND";
    case HelloErrorCode.INVALID_REQUEST:
      return "INVALID_REQUEST";
    case HelloErrorCode.UNAVAILABLE:
      return "UNAVAILABLE";
    case HelloErrorCode.NATS_STREAM_NOT_FOUND:
      return "NATS_STREAM_NOT_FOUND";
    case HelloErrorCode.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/**
 * RpcFrameType names the frames of a call. 1-3 are the original unary set and keep
 * their numbers and meanings exactly: a single-request/single-response call is
 * byte-identical on the wire before and after streaming existed.
 *
 * 4-8 carry streaming. A receiver that predates them drops an unknown frameType in
 * the default arm it already has, which is the whole compatibility story.
 */
export enum RpcFrameType {
  UnspecifiedRFT = 0,
  /** Request - opens a call; for a single-request call it IS the request */
  Request = 1,
  /** SuccessResponse - terminal: the response of a single-response call */
  SuccessResponse = 2,
  /** ErrorResponse - terminal: the error of ANY call, unary or streaming */
  ErrorResponse = 3,
  /** StreamData - One element of a stream, in either direction. */
  StreamData = 4,
  /**
   * StreamEnd - Half-close by the sender: "no more StreamData from me". Terminal for that
   * DIRECTION; each direction of a call ends on exactly one of StreamEnd or
   * ErrorResponse.
   */
  StreamEnd = 5,
  /**
   * Cancel - The caller abandons the call. The callee cancels its handler and still answers
   * with its one terminal frame (ErrorResponse, RPC/CANCELLED) — so the caller can
   * release per-call state on one deterministic event either way.
   */
  Cancel = 6,
  /**
   * Heartbeat - Liveness while a call is open but not yet producing: a query that takes three
   * minutes before its first row would otherwise trip the idle deadline. Carries no
   * data and never appears in the frameSeq sequence.
   */
  Heartbeat = 7,
  /**
   * StreamAccepted - The callee's acknowledgement that a streaming call opened, sent once and first,
   * for streaming call kinds only — a single-request/single-response call never sees
   * it. Its header Sender is the address the caller sends every later frame of the
   * call to (StreamData, StreamEnd, Cancel): a call is opened at a name and continued
   * at the address that answered, so several processes can read one named inbox.
   * Carries no data and never appears in the frameSeq sequence.
   * FEATURE-20260704-shared-named-mailbox.
   */
  StreamAccepted = 8,
  UNRECOGNIZED = -1,
}

export function rpcFrameTypeFromJSON(object: any): RpcFrameType {
  switch (object) {
    case 0:
    case "UnspecifiedRFT":
      return RpcFrameType.UnspecifiedRFT;
    case 1:
    case "Request":
      return RpcFrameType.Request;
    case 2:
    case "SuccessResponse":
      return RpcFrameType.SuccessResponse;
    case 3:
    case "ErrorResponse":
      return RpcFrameType.ErrorResponse;
    case 4:
    case "StreamData":
      return RpcFrameType.StreamData;
    case 5:
    case "StreamEnd":
      return RpcFrameType.StreamEnd;
    case 6:
    case "Cancel":
      return RpcFrameType.Cancel;
    case 7:
    case "Heartbeat":
      return RpcFrameType.Heartbeat;
    case 8:
    case "StreamAccepted":
      return RpcFrameType.StreamAccepted;
    case -1:
    case "UNRECOGNIZED":
    default:
      return RpcFrameType.UNRECOGNIZED;
  }
}

export function rpcFrameTypeToJSON(object: RpcFrameType): string {
  switch (object) {
    case RpcFrameType.UnspecifiedRFT:
      return "UnspecifiedRFT";
    case RpcFrameType.Request:
      return "Request";
    case RpcFrameType.SuccessResponse:
      return "SuccessResponse";
    case RpcFrameType.ErrorResponse:
      return "ErrorResponse";
    case RpcFrameType.StreamData:
      return "StreamData";
    case RpcFrameType.StreamEnd:
      return "StreamEnd";
    case RpcFrameType.Cancel:
      return "Cancel";
    case RpcFrameType.Heartbeat:
      return "Heartbeat";
    case RpcFrameType.StreamAccepted:
      return "StreamAccepted";
    case RpcFrameType.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/**
 * RpcCallKind is the product (single | streaming request) x (single | streaming
 * response) — four shapes, one frame grammar. Which shape an endpoint is belongs to
 * the endpoint (the handler registration fixes it); the caller states it so a
 * mismatch is caught at the Request instead of mid-stream.
 *
 * The composition rule, which is why no shape needs a frame the others do not:
 *   single request   -> the Request frame IS the whole request stream (no StreamEnd)
 *   streaming request-> Request (element 1), StreamData*, StreamEnd
 *   single response  -> SuccessResponse | ErrorResponse
 *   streaming resp   -> StreamData*, StreamEnd | ErrorResponse
 */
export enum RpcCallKind {
  /** UnspecifiedCK - == SingleSingle; what every pre-streaming client sends */
  UnspecifiedCK = 0,
  /** SingleSingle - unary: the fast path, unchanged */
  SingleSingle = 1,
  SingleReqStreamingResp = 2,
  StreamingReqSingleResp = 3,
  StreamingReqStreamingResp = 4,
  UNRECOGNIZED = -1,
}

export function rpcCallKindFromJSON(object: any): RpcCallKind {
  switch (object) {
    case 0:
    case "UnspecifiedCK":
      return RpcCallKind.UnspecifiedCK;
    case 1:
    case "SingleSingle":
      return RpcCallKind.SingleSingle;
    case 2:
    case "SingleReqStreamingResp":
      return RpcCallKind.SingleReqStreamingResp;
    case 3:
    case "StreamingReqSingleResp":
      return RpcCallKind.StreamingReqSingleResp;
    case 4:
    case "StreamingReqStreamingResp":
      return RpcCallKind.StreamingReqStreamingResp;
    case -1:
    case "UNRECOGNIZED":
    default:
      return RpcCallKind.UNRECOGNIZED;
  }
}

export function rpcCallKindToJSON(object: RpcCallKind): string {
  switch (object) {
    case RpcCallKind.UnspecifiedCK:
      return "UnspecifiedCK";
    case RpcCallKind.SingleSingle:
      return "SingleSingle";
    case RpcCallKind.SingleReqStreamingResp:
      return "SingleReqStreamingResp";
    case RpcCallKind.StreamingReqSingleResp:
      return "StreamingReqSingleResp";
    case RpcCallKind.StreamingReqStreamingResp:
      return "StreamingReqStreamingResp";
    case RpcCallKind.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export enum ContentType {
  UnspecifiedCT = 0,
  Protobuf = 1,
  Json = 2,
  Binary = 3,
  Text = 4,
  UNRECOGNIZED = -1,
}

export function contentTypeFromJSON(object: any): ContentType {
  switch (object) {
    case 0:
    case "UnspecifiedCT":
      return ContentType.UnspecifiedCT;
    case 1:
    case "Protobuf":
      return ContentType.Protobuf;
    case 2:
    case "Json":
      return ContentType.Json;
    case 3:
    case "Binary":
      return ContentType.Binary;
    case 4:
    case "Text":
      return ContentType.Text;
    case -1:
    case "UNRECOGNIZED":
    default:
      return ContentType.UNRECOGNIZED;
  }
}

export function contentTypeToJSON(object: ContentType): string {
  switch (object) {
    case ContentType.UnspecifiedCT:
      return "UnspecifiedCT";
    case ContentType.Protobuf:
      return "Protobuf";
    case ContentType.Json:
      return "Json";
    case ContentType.Binary:
      return "Binary";
    case ContentType.Text:
      return "Text";
    case ContentType.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export interface MessageFromClient {
  sendMessageRequest?: SendMessageRequest | undefined;
  firstMessage?: FirstMessage | undefined;
  ping?: Ping | undefined;
  pong?: Pong | undefined;
  notification?: Notification | undefined;
  subscribeRequest?: SubscribeRequest | undefined;
  publishToContinuumCentral?: PublishToContinuumCentral | undefined;
  browserSessionStart?: BrowserSessionStart | undefined;
  clientSessionStart?: ClientSessionStart | undefined;
  clientHello?: ClientHello | undefined;
  loginComplete?:
    | LoginComplete
    | undefined;
  /**
   * DEV-LOOP ARMS. Both are refused unless the server was started with
   * a8.devLoop.enabled AND the caller holds ContinuumAdmin — two independent
   * conditions, so in production the honest answer is "the arm does not exist"
   * rather than "the arm exists and is guarded".
   * FEATURE-20260808-ws-publish-subscribe-loopback-devtools-client
   */
  publishToSubject?: PublishToSubject | undefined;
  createTopic?: CreateTopic | undefined;
}

/**
 * ClientHello is THE first frame on every WebSocket. One message, three variants — start a
 * browser session, start a process session, or resume a mailbox that already exists — plus
 * the subscriptions this connection wants.
 *
 * It replaces the accreted handshake it supersedes: FirstMessage as the first frame, with
 * ClientSessionStart bolted on ahead of it for WS-native bootstrap. That shape cost TWO
 * round-trips (mint, then re-send the keys the gateway had just minted, only to subscribe)
 * and made the connection's CLASS depend on which message happened to arrive first — which
 * the WS gateway trust model (§2) calls "an accident of implementation, not a trust model".
 * Here the class is an explicit oneof, and create+subscribe happen in one frame.
 *
 * Spec: docs/2026-07/20260724-ws-client-protocol.md
 */
export interface ClientHello {
  /** no mailbox yet; requires ContinuumUser */
  browserSession?:
    | BrowserSessionStart
    | undefined;
  /** no mailbox yet; ContinuumAdmin or a worker */
  processSession?:
    | ProcessSessionStart
    | undefined;
  /** mailbox already exists */
  resumeSession?:
    | ResumeSession
    | undefined;
  /**
   * What this connection wants to hear — applied for every variant, so a fresh session
   * subscribes in the SAME frame that creates it. On a hello, MailboxSubscription.readerKey
   * may be left empty: the gateway just resolved or minted the mailbox and knows it.
   */
  subscriptions: Subscription[];
}

/**
 * ProcessSessionStart is the CLASS-3 (CLI / daemon) variant. It carries the REAL
 * ProcessStartedRequest rather than a hand-picked mirror of one, so jobUid, eventUid,
 * discovery, process-manager fields, pid, cwd and command all survive — a mirror was tried
 * and silently dropped every one of them, and would drift on each new field.
 *
 * Trust model §5 says the gateway CONSTRUCTS lifecycle messages and never relays
 * client-marshaled ones. This obeys that where it matters: the gateway OVERWRITES every
 * field carrying AUTHORITY — workerUid from the authenticated identity, kind forced (never
 * the gateway-only browser kind), server-observed IP stamped over extraData — and passes
 * through only DESCRIPTIVE fields. The invariant is "authority is server-derived", not
 * "a client may not describe itself".
 */
export interface ProcessSessionStart {
  process: ProcessStartedRequest | undefined;
  lifecycle: MailboxLifecycle;
  channels: string[];
  authToken?: string | undefined;
  loginBegin?: LoginBeginRequest | undefined;
}

/**
 * ResumeSession reattaches to a mailbox that already exists. This is what FirstMessage
 * becomes: readerKey is the only key needed (the gateway resolves the mailbox from it), so
 * FirstMessage's senderInfo.address is gone — the server compared the client's claimed
 * address to what it had just looked up, learning nothing. mailboxTimeoutInMs and meta are
 * gone too: both were read nowhere.
 *
 * A RESUME MUST NOT MINT. Re-bootstrapping on reconnect creates a second mailbox and
 * orphans the first — the failure that left 459 dead mailboxes on 2026-07-07. If the
 * mailbox is genuinely gone the gateway answers MAILBOX_NOT_FOUND and the client decides
 * deliberately whether to start a new session.
 */
export interface ResumeSession {
  readerKey: string;
  authToken: string;
}

/**
 * LoginComplete carries the client's signature over the nonce from LoginChallenge. Only
 * used on the inline-login path.
 */
export interface LoginComplete {
  sessionId: string;
  signature: Uint8Array;
}

/** LoginChallenge is the nonce a client must sign. Only on the inline-login path. */
export interface LoginChallenge {
  sessionId: string;
  nonce: Uint8Array;
}

/**
 * HelloError is a CODED refusal. Notification (free text) is too weak for a handshake with
 * a challenge-response in the middle: a client has to tell "give up" from "retry" without
 * parsing English.
 */
export interface HelloError {
  code: HelloErrorCode;
  /** human text; clients MUST NOT parse it */
  message: string;
}

/**
 * ClientSessionStart is a CLASS-3 (CLI / daemon) session bootstrapping ITSELF over the
 * WebSocket: the client connects authenticated but with NO mailbox, and the gateway mints
 * the processrun AND the mailbox server-side and binds them to THIS connection. The same
 * socket then carries the mailbox for everything after — one authenticated, load-balanced
 * connection doing bootstrap and runtime.
 * See tracker FEATURE-20260724-ws-native-bootstrap-and-mailbox-transport.
 *
 * WHY THIS EXISTS: the WS FirstMessage handshake resolves a mailbox BY READER KEY and
 * fails the connection when it is absent, so a WS connection has always required a
 * mailbox that already existed — which is why a CLI had to create one over HTTP first and
 * then open a WS for the data plane only. This message removes that ordering constraint.
 *
 * TRUST BOUNDARY — the CLI is an untrusted client, exactly like the browser, but unlike a
 * browser it legitimately HAS a worker identity. So rather than omitting the field (the
 * browser's forge-proof-by-construction trick, which works because a browser has no
 * worker), workerUid is present but OWNERSHIP-CHECKED: the gateway verifies the claimed
 * worker is owned by the AUTHENTICATED user (the workerOwnedByUser check already used for
 * the lifecycle relay) and refuses otherwise. A client may CLAIM a worker; it may not
 * claim one it does not own. kind is forced server-side and can never be the gateway-only
 * browser kind. There is deliberately NO jobUid/eventUid slot here: bootstrap does not
 * need them, and a field a client cannot set is a field it cannot forge.
 */
export interface ClientSessionStart {
  /**
   * authToken: the caller's auth token (from SSH/oauth login). This message is the FIRST
   * thing a WS-native client sends — it arrives BEFORE FirstMessage, because FirstMessage
   * resolves an existing mailbox and this is the message that creates one. So it cannot
   * rely on the connection already being authenticated; it carries and proves its own
   * identity, validated through the same AuthProvider the HTTP endpoints use.
   */
  authToken: string;
  browser?: BrowserSessionStart | undefined;
  process?:
    | ProcessStartedRequest
    | undefined;
  /**
   * lifecycleKind: short-lived-cli | long-lived-daemon — the coarse mailbox lifecycle the
   * client declares; the server resolves it to concrete purge/close timeouts. A MAILBOX
   * concern, which is why it is here rather than on the session message.
   */
  lifecycleKind: string;
  /**
   * channels: the mailbox channels to create (rpc-inbox, ...). Server-validated. Also a
   * mailbox concern.
   */
  channels: string[];
}

/**
 * ClientSessionStarted is the gateway's reply to ClientSessionStart: the keys of the
 * mailbox it just minted, so the client can use the mailbox it never created itself.
 * The mailbox is ALREADY bound to this connection when this is sent.
 */
export interface ClientSessionStarted {
  address: string;
  adminKey: string;
  readerKey: string;
  channels: string[];
  /**
   * processUid: the row the gateway committed. MINTED by the gateway for a browser
   * session; echoed back for a process session, which supplies its own.
   */
  processUid: string;
  /**
   * workerUid: the identity the gateway RESOLVED for this connection, returned so a client
   * learns who it is from the exchange instead of asserting it. Empty when the identity is
   * not a worker (a human at a CLI, a browser user).
   */
  workerUid: string;
  /**
   * authToken: the token the gateway MINTED during inline (SSH) login, returned for the same
   * reason workerUid is — a client should learn what the auth exchange produced rather than
   * be unable to name it.
   *
   * Without this a client that authenticated inline holds NO token, so ResumeSession presents
   * an empty one and the gateway answers UNAUTHENTICATED — a resume that cannot possibly
   * succeed. Measured on hermes: 17,900 of 20,000 messages lost on kill-mid-request, entirely
   * because its (correct) resume could never authenticate.
   *
   * Empty when the client supplied its own token: it already has one, and echoing it back
   * would put a credential on the wire for no reason.
   */
  authToken: string;
}

/**
 * BrowserSessionStart is a CLASS-1 (browser app) session announcing itself so the
 * gateway can give it a processrun and fold it into the standard process lifecycle
 * (ledger reclaim on completion, AWOL when its pings stop). See tracker
 * FEATURE-20260714-browser-session-mailbox-accounting.
 *
 * This is deliberately NOT MessageFromRunner: a browser is an UNTRUSTED client and
 * must not marshal its own ProcessStart. It carries ONLY the fields a browser
 * legitimately knows — there is no workerUid / jobUid / jobName / eventUid slot
 * here, so a browser CANNOT set them (forge-proof by construction, not by
 * validation). The gateway TRANSLATES this into a ProcessStart server-side: it
 * forces kind=browser, leaves worker/job/event empty (worker identity resolves
 * later through the authenticated mailbox, as it already does for any process —
 * bootstrap.go:362), and stamps the server-captured IP + User-Agent (ClientInfo,
 * godev@e10a1fdb) into extraData. Class-2 CLI-over-WS clients keep using the
 * verbatim PublishToContinuumCentral relay; the two paths are distinct on purpose
 * (see tracker TASK-20260715-ws-client-class-audit).
 */
export interface BrowserSessionStart {
  /**
   * tabUid: an immutable per-TAB uid, minted once by the browser when the tab
   * loads and stable for the tab's whole life. It is the correlation key ABOVE the
   * mailbox: one tab churns through many mailboxes (dedupe + re-mint-on-wake), all
   * sharing one tabUid. Client-generated — a correlation label, not a credential.
   */
  tabUid: string;
  /**
   * mainName: which browser "main" / app entry point this session runs (there are
   * several). Lands in the processrun's discovery/category so browser apps are
   * distinguishable. Client-supplied label.
   */
  mainName: string;
  /** startedAt: when this browser session began (client clock). */
  startedAt:
    | Date
    | undefined;
  /**
   * The page context, TOP-LEVEL and typed. These previously lived inside an untyped
   * extraData struct alongside the server's own facts, so "who set this" was a convention
   * rather than something the schema stated.
   */
  url: string;
  referer: string;
  locale: string;
  timezone: string;
  /** authToken: obtained out of band via OAuth. A browser does not do the SSH challenge. */
  authToken: string;
  /**
   * buildInfo: the BUNDLE's build identity (codebase, version, git commit/branch/dirty,
   * build timestamp), stamped into the bundle at vite build time. Client-provided and a
   * LABEL like tabUid, never a credential — and the bundle is the only party that CAN
   * know its own build. This is what lets the registry tell two deployed bundle builds
   * apart (FEATURE-20260802-browser-bundle-build-identity).
   */
  buildInfo:
    | BuildInfo
    | undefined;
  /** --- SERVER-POLYFILLED from the request: a client value here is IGNORED, not merged. --- */
  ip: string;
  userAgent: string;
  /**
   * extraData: anything else client-known that has no field of its own. Server-authoritative
   * facts are NOT merged into this any more — they have real fields above.
   */
  extraData: { [key: string]: any } | undefined;
}

/**
 * PublishToContinuumCentral relays a runner process-lifecycle message
 * (continuum_rpc.MessageFromRunner: ProcessStart/Ping/Complete) from a WS client
 * to the NATS continuum.central subject, so a WS-only client emits lifecycle
 * identically to a NATS client (which publishes to continuum.central directly).
 *
 * The gateway authenticates the WS mailbox and VERIFIES that workerUid matches the
 * authenticated worker before publishing; a mismatch is an error and the message is
 * NOT relayed (no rewriting — verify and reject). messageFromRunnerBytes is the
 * protobuf-marshaled continuum_rpc.MessageFromRunner (carried as bytes so the mesh
 * proto need not depend on continuum_rpc); the gateway publishes it VERBATIM.
 */
export interface PublishToContinuumCentral {
  /** the worker this lifecycle is for; gateway checks == authed worker */
  workerUid: string;
  /** marshaled continuum_rpc.MessageFromRunner, relayed verbatim */
  messageFromRunnerBytes: Uint8Array;
}

export interface Notification {
  message: string;
}

export interface MessageToClient {
  messageEnvelope?: MessageEnvelope | undefined;
  sendMessageResponse?: SendMessageResponse | undefined;
  ping?: Ping | undefined;
  pong?: Pong | undefined;
  notification?: Notification | undefined;
  subscribeResponse?: SubscribeResponse | undefined;
  clientSessionStarted?: ClientSessionStarted | undefined;
  loginChallenge?: LoginChallenge | undefined;
  helloError?: HelloError | undefined;
  devLoopResponse?: DevLoopResponse | undefined;
}

export interface Ping {
  payload: Uint8Array;
}

export interface Pong {
  payload: Uint8Array;
}

export interface MessageHeader {
  sender: string;
  contentType: ContentType;
  rpcHeader: RpcHeader | undefined;
  senderSequence: string;
  extraHeaders: KeyValPair[];
}

export interface SenderEnvelope {
  created: string;
}

export interface ServerEnvelope {
  sequence: string;
  created: string;
  channel: string;
  subscriptionId: string;
}

export interface KeyValPair {
  key: string;
  val: string;
}

export interface RpcHeader {
  /**
   * Correlates the end-to-end request. Every frame of a streaming call carries the
   * SAME correlationId as the Request that opened it — that is what makes a call a
   * call rather than a pile of messages.
   */
  correlationId: string;
  /** Logical RPC endpoint name (e.g. "UserService.CreateUser"). */
  endPoint: string;
  /** Which frame of the call this is; see RpcFrameType. */
  frameType: RpcFrameType;
  /** Populated when frameType == ErrorResponse. */
  errorInfo:
    | RpcErrorInfo
    | undefined;
  /**
   * Which of the four call shapes this call is. Set on the REQUEST frame only; the
   * frames that follow inherit it from the call. Unset (0) means SingleSingle, so
   * every client that predates streaming keeps working without sending a new field —
   * and a unary frame is byte-identical to what those clients send today.
   *
   * The server refuses a Request whose callKind disagrees with the registered
   * handler's shape (BAD_REQUEST), BEFORE any stream frame moves. That refusal is
   * what keeps an old unary client from being handed a StreamData it would treat as
   * the response. FEATURE-20260901-mesh-streaming-rpc.
   */
  callKind: RpcCallKind;
  /**
   * 1-based, contiguous, per (correlationId, DIRECTION). Set on StreamData and on the
   * StreamEnd that closes that direction (StreamEnd carries last+1, so a receiver can
   * prove nothing was lost rather than assuming it). Unset on Request and on the
   * single-response terminals.
   *
   * Receiver rule: seq < expected -> a duplicate, drop it (delivery is at-least-once,
   * so duplicates are LEGAL); seq == expected -> deliver; seq > expected -> a GAP, and
   * the call fails locally. There is deliberately no reorder buffer: one publisher on
   * one connection into one JetStream stream is ordered, so a gap is real loss, not
   * reordering, and a buffer would hide it.
   */
  frameSeq: string;
  /**
   * Human text on a Cancel frame, for the log at the other end. Clients MUST NOT
   * parse it.
   */
  cancelReason: string;
}

/** Optional source location for debugging. */
export interface ErrorLocation {
  file: string;
  line: number;
  function: string;
}

/** One layer's view of an error. */
export interface ErrorFrame {
  /**
   * High-level error category. Allowed values:
   * "RPC", "AUTH", "CLIENT", "RESOURCE", "DEPENDENCY", "INTERNAL".
   */
  category: string;
  /**
   * Category-local kind identifier. Allowed values per category include:
   *   RPC:        "BAD_REQUEST", "TIMEOUT", "CANCELLED"
   *   AUTH:       "UNAUTHENTICATED", "PERMISSION_DENIED"
   *   CLIENT:     "INVALID_ARGUMENT", "NOT_FOUND", "CONFLICT"
   *   RESOURCE:   "RATE_LIMITED", "QUOTA_EXCEEDED", "OVERLOADED"
   *   DEPENDENCY: "DB_UNAVAILABLE", "NATS_UNAVAILABLE", "UPSTREAM_UNAVAILABLE"
   *   INTERNAL:   "UNEXPECTED", "INVARIANT_VIOLATION"
   * Additional kinds may be added over time but must preserve semantics.
   */
  kind: string;
  /** Human-readable message. Not for parsing. */
  message: string;
  /** Whether a blind retry is reasonable without changing inputs. */
  retryable: boolean;
  /** Free-form structured details (JSON-like). Use this instead of parsing "message". */
  details:
    | { [key: string]: any }
    | undefined;
  /** Logical component name, e.g. "user-service", "batch-worker". */
  component: string;
  /** Optional source location for debugging. */
  location:
    | ErrorLocation
    | undefined;
  /** Creation time (UTC). */
  timestamp: Date | undefined;
}

/**
 * A node in the error tree. This composes recursively.
 *
 * Semantics:
 * - There is a single top-level ErrorNode for a logical error.
 * - Each ErrorNode has 0 or more "causes", each of which is itself an ErrorNode.
 *   This allows building a chain or tree of causes without special-casing.
 */
export interface ErrorNode {
  frame:
    | ErrorFrame
    | undefined;
  /**
   * Zero or more direct causes of this error. Each cause is a full ErrorNode
   * and can itself have further causes.
   */
  causes: ErrorNode[];
}

/**
 * Error info attached to an RpcHeader when frameType == ErrorResponse.
 *
 * For backward compatibility, the legacy fields (errorCode, message, stackTrace) are preserved.
 * New code SHOULD use error_node instead.
 *
 * - errorCode, message, stackTrace: legacy fields (deprecated but functional)
 * - error_node: new preferred format with rich error tree structure
 */
export interface RpcErrorInfo {
  /** Legacy fields (deprecated but maintained for backward compatibility) */
  errorCode: number;
  message: string;
  stackTrace: string;
  /** New error model (preferred) - single error with optional causes */
  errorNode: ErrorNode | undefined;
}

export interface Message {
  header: MessageHeader | undefined;
  senderEnvelope: SenderEnvelope | undefined;
  serverEnvelope: ServerEnvelope | undefined;
  data: Uint8Array;
  dataRef: string;
}

export interface MessageEnvelope {
  messageBytes: Uint8Array;
  serverEnvelope: ServerEnvelope | undefined;
}

export interface SendMessageRequest {
  to: string[];
  message: Message | undefined;
  channel: string;
  idempotentId: string;
}

export interface SendMessageResponse {
  errors: SendMessageError[];
  duplicates: string[];
  idempotentId: string;
  correlationId: string;
}

export interface SendReceipt {
  request: SendMessageRequest | undefined;
  response: SendMessageResponse | undefined;
  serverEnvelope: ServerEnvelope | undefined;
}

export interface SendMessageError {
  message: string;
  to: string;
}

export interface FirstMessage {
  senderInfo: SenderInfo | undefined;
  mailboxTimeoutInMs: number;
  subscriptions: Subscription[];
  authToken: string;
  meta: { [key: string]: any } | undefined;
}

export interface SenderInfo {
  readerKey: string;
  address: string;
}

export interface SubscribeRequest {
  subscriptions: Subscription[];
}

export interface SubscribeResponse {
  succeeded: string[];
  errors: SubscribeError[];
}

export interface SubscribeError {
  id: string;
  state: string;
  message: string;
}

export interface Subscription {
  mailbox?: MailboxSubscription | undefined;
  continuum?: ContinuumSubscription | undefined;
  changeDataCapture?: ChangeDataCaptureSubscription | undefined;
  unsubscribe?:
    | Unsubscribe
    | undefined;
  /**
   * A DEV-LOOP ARM, not a general subscription kind — see SubjectSubscription.
   * FEATURE-20260808-ws-subscribe-to-subject-closes-the-devloop
   */
  subject?: SubjectSubscription | undefined;
}

export interface MailboxSubscription {
  id: string;
  state: string;
  readerKey: string;
  channel: string;
  startSeq: string;
  endSeq: string;
}

export interface ChangeDataCaptureSubscription {
  id: string;
  state: string;
  matchers: RecordMatcher[];
  startSeq: string;
  endSeq: string;
}

export interface RecordMatcher {
  database: string;
  table: string;
  primaryKeys: any[];
}

export interface ContinuumSubscription {
  id: string;
  state: string;
  processUid: string;
  channel: string;
  startSeq: string;
  endSeq: string;
}

export interface Unsubscribe {
  id: string;
}

/**
 * SubjectSubscription is the RECEIVE half of the dev loop: it delivers whatever lands on a NATS
 * subject to this websocket, so one socket can publish and consume its own traffic and produce a
 * real roundtrip number rather than a publish-ack latency.
 *
 * IT IS A DEV-LOOP ARM, gated exactly as PublishToSubject is — devLoop.enabled AND
 * ContinuumAdmin AND the configured subject prefix. That is deliberately stricter than the
 * subscription kinds beside it, because subject-addressed READ is the widest capability in this
 * oneof: mailbox is readerKey-scoped, continuum is checked against RankView on the owning job,
 * and changeDataCapture is one named firehose behind a role. This one is "point me at any subject
 * and show me the traffic". In production the arm does not exist at all.
 *
 * streamName is REQUIRED and is not a detail a caller should have to guess: delivery reads
 * through JetStream, so a subject with no stream over it yields silence — the gateway verifies
 * the stream exists AND covers the subject before answering SUCCEEDED, rather than registering a
 * subscription that waits forever for a stream nobody will create.
 * FEATURE-20260808-ws-subscribe-to-subject-closes-the-devloop
 */
export interface SubjectSubscription {
  id: string;
  state: string;
  subject: string;
  streamName: string;
  startSeq: string;
  endSeq: string;
}

/**
 * PublishToSubject publishes a payload onto a NATS subject, so ONE websocket can push and pull
 * at the same time and produce real roundtrip numbers.
 *
 * This is a general publish, which is a much wider authority than the arms beside it —
 * PublishToContinuumCentral targets one destination and verifies the caller owns the worker uid
 * it claims, and SendMessageRequest is mailbox-addressed. So it is gated twice: the server must
 * have a8.devLoop.enabled AND the caller must hold ContinuumAdmin.
 *
 * `subject` is constrained to the configured prefix (default "devloop."). A loopback harness
 * needs one subject tree, not the estate, and a prefix check is the difference between a
 * measurement rig and a NATS publish proxy for whoever holds the role.
 */
export interface PublishToSubject {
  subject: string;
  payload: Uint8Array;
  /**
   * Echoed back on the DevLoopResponse so a publisher can correlate without parsing its own
   * payload — the roundtrip measurement wants a sequence number it did not have to invent twice.
   */
  token: string;
}

/**
 * CreateTopic creates a JetStream stream for the dev loop to publish into.
 *
 * Separate from PublishToSubject on purpose: publishing is per-message and cheap, creating a
 * stream is estate-shaped and persists after the connection closes. Same double gate.
 */
export interface CreateTopic {
  name: string;
  subjects: string[];
}

/**
 * DevLoopResponse answers both dev-loop arms.
 *
 * One response type rather than two, because the client's correlation is the token and the only
 * thing it needs to know is whether the operation happened and why not. `kind` names which arm
 * answered so a client driving both does not have to infer it from context.
 */
export interface DevLoopResponse {
  /** "publishToSubject" | "createTopic" */
  kind: string;
  token: string;
  ok: boolean;
  /** populated when ok=false; the REASON, not just a failure */
  message: string;
}

export interface CreateMailboxRequest {
  channels: string[];
  privateMetadata: { [key: string]: any } | undefined;
  publicMetadata: { [key: string]: any } | undefined;
  purgeTimeoutInMillis: string;
  closeTimeoutInMillis: string;
  extraData:
    | { [key: string]: any }
    | undefined;
  /**
   * Coarse, ergonomic lifecycle declaration ("short-lived-cli" | "long-lived-daemon").
   * Creation input only (not stored on the wire): the server resolves it to concrete
   * purge/close timeouts, which ARE stored. Empty = short-lived-cli default.
   */
  lifecycleKind: string;
  /**
   * Owning processrun uid, when the creator has one backed by a processrun record
   * (the client's processUid IS the future processrun.uid, verbatim). Stored on the
   * mailbox so aliveness can be checked against the actual process. Empty when the
   * creator has no processrun (e.g. a bare CLI) — then only the idle timeout applies.
   */
  processRunUid: string;
}

export interface CreateMailboxResponse {
  adminKey: string;
  address: string;
  readerKey: string;
  channels: string[];
}

export interface AddChannelRequest {
  adminKey: string;
  channels: string[];
}

export interface AddChannelResponse {
}

function createBaseMessageFromClient(): MessageFromClient {
  return {
    sendMessageRequest: undefined,
    firstMessage: undefined,
    ping: undefined,
    pong: undefined,
    notification: undefined,
    subscribeRequest: undefined,
    publishToContinuumCentral: undefined,
    browserSessionStart: undefined,
    clientSessionStart: undefined,
    clientHello: undefined,
    loginComplete: undefined,
    publishToSubject: undefined,
    createTopic: undefined,
  };
}

export const MessageFromClient = {
  encode(message: MessageFromClient, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.sendMessageRequest !== undefined) {
      SendMessageRequest.encode(message.sendMessageRequest, writer.uint32(10).fork()).ldelim();
    }
    if (message.firstMessage !== undefined) {
      FirstMessage.encode(message.firstMessage, writer.uint32(18).fork()).ldelim();
    }
    if (message.ping !== undefined) {
      Ping.encode(message.ping, writer.uint32(26).fork()).ldelim();
    }
    if (message.pong !== undefined) {
      Pong.encode(message.pong, writer.uint32(34).fork()).ldelim();
    }
    if (message.notification !== undefined) {
      Notification.encode(message.notification, writer.uint32(42).fork()).ldelim();
    }
    if (message.subscribeRequest !== undefined) {
      SubscribeRequest.encode(message.subscribeRequest, writer.uint32(50).fork()).ldelim();
    }
    if (message.publishToContinuumCentral !== undefined) {
      PublishToContinuumCentral.encode(message.publishToContinuumCentral, writer.uint32(58).fork()).ldelim();
    }
    if (message.browserSessionStart !== undefined) {
      BrowserSessionStart.encode(message.browserSessionStart, writer.uint32(66).fork()).ldelim();
    }
    if (message.clientSessionStart !== undefined) {
      ClientSessionStart.encode(message.clientSessionStart, writer.uint32(74).fork()).ldelim();
    }
    if (message.clientHello !== undefined) {
      ClientHello.encode(message.clientHello, writer.uint32(82).fork()).ldelim();
    }
    if (message.loginComplete !== undefined) {
      LoginComplete.encode(message.loginComplete, writer.uint32(90).fork()).ldelim();
    }
    if (message.publishToSubject !== undefined) {
      PublishToSubject.encode(message.publishToSubject, writer.uint32(98).fork()).ldelim();
    }
    if (message.createTopic !== undefined) {
      CreateTopic.encode(message.createTopic, writer.uint32(106).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MessageFromClient {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMessageFromClient();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.sendMessageRequest = SendMessageRequest.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.firstMessage = FirstMessage.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.ping = Ping.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.pong = Pong.decode(reader, reader.uint32());
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.notification = Notification.decode(reader, reader.uint32());
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.subscribeRequest = SubscribeRequest.decode(reader, reader.uint32());
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.publishToContinuumCentral = PublishToContinuumCentral.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.browserSessionStart = BrowserSessionStart.decode(reader, reader.uint32());
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.clientSessionStart = ClientSessionStart.decode(reader, reader.uint32());
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.clientHello = ClientHello.decode(reader, reader.uint32());
          continue;
        case 11:
          if (tag !== 90) {
            break;
          }

          message.loginComplete = LoginComplete.decode(reader, reader.uint32());
          continue;
        case 12:
          if (tag !== 98) {
            break;
          }

          message.publishToSubject = PublishToSubject.decode(reader, reader.uint32());
          continue;
        case 13:
          if (tag !== 106) {
            break;
          }

          message.createTopic = CreateTopic.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): MessageFromClient {
    return {
      sendMessageRequest: isSet(object.sendMessageRequest)
        ? SendMessageRequest.fromJSON(object.sendMessageRequest)
        : undefined,
      firstMessage: isSet(object.firstMessage) ? FirstMessage.fromJSON(object.firstMessage) : undefined,
      ping: isSet(object.ping) ? Ping.fromJSON(object.ping) : undefined,
      pong: isSet(object.pong) ? Pong.fromJSON(object.pong) : undefined,
      notification: isSet(object.notification) ? Notification.fromJSON(object.notification) : undefined,
      subscribeRequest: isSet(object.subscribeRequest) ? SubscribeRequest.fromJSON(object.subscribeRequest) : undefined,
      publishToContinuumCentral: isSet(object.publishToContinuumCentral)
        ? PublishToContinuumCentral.fromJSON(object.publishToContinuumCentral)
        : undefined,
      browserSessionStart: isSet(object.browserSessionStart)
        ? BrowserSessionStart.fromJSON(object.browserSessionStart)
        : undefined,
      clientSessionStart: isSet(object.clientSessionStart)
        ? ClientSessionStart.fromJSON(object.clientSessionStart)
        : undefined,
      clientHello: isSet(object.clientHello) ? ClientHello.fromJSON(object.clientHello) : undefined,
      loginComplete: isSet(object.loginComplete) ? LoginComplete.fromJSON(object.loginComplete) : undefined,
      publishToSubject: isSet(object.publishToSubject) ? PublishToSubject.fromJSON(object.publishToSubject) : undefined,
      createTopic: isSet(object.createTopic) ? CreateTopic.fromJSON(object.createTopic) : undefined,
    };
  },

  toJSON(message: MessageFromClient): unknown {
    const obj: any = {};
    if (message.sendMessageRequest !== undefined) {
      obj.sendMessageRequest = SendMessageRequest.toJSON(message.sendMessageRequest);
    }
    if (message.firstMessage !== undefined) {
      obj.firstMessage = FirstMessage.toJSON(message.firstMessage);
    }
    if (message.ping !== undefined) {
      obj.ping = Ping.toJSON(message.ping);
    }
    if (message.pong !== undefined) {
      obj.pong = Pong.toJSON(message.pong);
    }
    if (message.notification !== undefined) {
      obj.notification = Notification.toJSON(message.notification);
    }
    if (message.subscribeRequest !== undefined) {
      obj.subscribeRequest = SubscribeRequest.toJSON(message.subscribeRequest);
    }
    if (message.publishToContinuumCentral !== undefined) {
      obj.publishToContinuumCentral = PublishToContinuumCentral.toJSON(message.publishToContinuumCentral);
    }
    if (message.browserSessionStart !== undefined) {
      obj.browserSessionStart = BrowserSessionStart.toJSON(message.browserSessionStart);
    }
    if (message.clientSessionStart !== undefined) {
      obj.clientSessionStart = ClientSessionStart.toJSON(message.clientSessionStart);
    }
    if (message.clientHello !== undefined) {
      obj.clientHello = ClientHello.toJSON(message.clientHello);
    }
    if (message.loginComplete !== undefined) {
      obj.loginComplete = LoginComplete.toJSON(message.loginComplete);
    }
    if (message.publishToSubject !== undefined) {
      obj.publishToSubject = PublishToSubject.toJSON(message.publishToSubject);
    }
    if (message.createTopic !== undefined) {
      obj.createTopic = CreateTopic.toJSON(message.createTopic);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<MessageFromClient>, I>>(base?: I): MessageFromClient {
    return MessageFromClient.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<MessageFromClient>, I>>(object: I): MessageFromClient {
    const message = createBaseMessageFromClient();
    message.sendMessageRequest = (object.sendMessageRequest !== undefined && object.sendMessageRequest !== null)
      ? SendMessageRequest.fromPartial(object.sendMessageRequest)
      : undefined;
    message.firstMessage = (object.firstMessage !== undefined && object.firstMessage !== null)
      ? FirstMessage.fromPartial(object.firstMessage)
      : undefined;
    message.ping = (object.ping !== undefined && object.ping !== null) ? Ping.fromPartial(object.ping) : undefined;
    message.pong = (object.pong !== undefined && object.pong !== null) ? Pong.fromPartial(object.pong) : undefined;
    message.notification = (object.notification !== undefined && object.notification !== null)
      ? Notification.fromPartial(object.notification)
      : undefined;
    message.subscribeRequest = (object.subscribeRequest !== undefined && object.subscribeRequest !== null)
      ? SubscribeRequest.fromPartial(object.subscribeRequest)
      : undefined;
    message.publishToContinuumCentral =
      (object.publishToContinuumCentral !== undefined && object.publishToContinuumCentral !== null)
        ? PublishToContinuumCentral.fromPartial(object.publishToContinuumCentral)
        : undefined;
    message.browserSessionStart = (object.browserSessionStart !== undefined && object.browserSessionStart !== null)
      ? BrowserSessionStart.fromPartial(object.browserSessionStart)
      : undefined;
    message.clientSessionStart = (object.clientSessionStart !== undefined && object.clientSessionStart !== null)
      ? ClientSessionStart.fromPartial(object.clientSessionStart)
      : undefined;
    message.clientHello = (object.clientHello !== undefined && object.clientHello !== null)
      ? ClientHello.fromPartial(object.clientHello)
      : undefined;
    message.loginComplete = (object.loginComplete !== undefined && object.loginComplete !== null)
      ? LoginComplete.fromPartial(object.loginComplete)
      : undefined;
    message.publishToSubject = (object.publishToSubject !== undefined && object.publishToSubject !== null)
      ? PublishToSubject.fromPartial(object.publishToSubject)
      : undefined;
    message.createTopic = (object.createTopic !== undefined && object.createTopic !== null)
      ? CreateTopic.fromPartial(object.createTopic)
      : undefined;
    return message;
  },
};

function createBaseClientHello(): ClientHello {
  return { browserSession: undefined, processSession: undefined, resumeSession: undefined, subscriptions: [] };
}

export const ClientHello = {
  encode(message: ClientHello, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.browserSession !== undefined) {
      BrowserSessionStart.encode(message.browserSession, writer.uint32(10).fork()).ldelim();
    }
    if (message.processSession !== undefined) {
      ProcessSessionStart.encode(message.processSession, writer.uint32(18).fork()).ldelim();
    }
    if (message.resumeSession !== undefined) {
      ResumeSession.encode(message.resumeSession, writer.uint32(26).fork()).ldelim();
    }
    for (const v of message.subscriptions) {
      Subscription.encode(v!, writer.uint32(34).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ClientHello {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseClientHello();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.browserSession = BrowserSessionStart.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.processSession = ProcessSessionStart.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.resumeSession = ResumeSession.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.subscriptions.push(Subscription.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ClientHello {
    return {
      browserSession: isSet(object.browserSession) ? BrowserSessionStart.fromJSON(object.browserSession) : undefined,
      processSession: isSet(object.processSession) ? ProcessSessionStart.fromJSON(object.processSession) : undefined,
      resumeSession: isSet(object.resumeSession) ? ResumeSession.fromJSON(object.resumeSession) : undefined,
      subscriptions: globalThis.Array.isArray(object?.subscriptions)
        ? object.subscriptions.map((e: any) => Subscription.fromJSON(e))
        : [],
    };
  },

  toJSON(message: ClientHello): unknown {
    const obj: any = {};
    if (message.browserSession !== undefined) {
      obj.browserSession = BrowserSessionStart.toJSON(message.browserSession);
    }
    if (message.processSession !== undefined) {
      obj.processSession = ProcessSessionStart.toJSON(message.processSession);
    }
    if (message.resumeSession !== undefined) {
      obj.resumeSession = ResumeSession.toJSON(message.resumeSession);
    }
    if (message.subscriptions?.length) {
      obj.subscriptions = message.subscriptions.map((e) => Subscription.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ClientHello>, I>>(base?: I): ClientHello {
    return ClientHello.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ClientHello>, I>>(object: I): ClientHello {
    const message = createBaseClientHello();
    message.browserSession = (object.browserSession !== undefined && object.browserSession !== null)
      ? BrowserSessionStart.fromPartial(object.browserSession)
      : undefined;
    message.processSession = (object.processSession !== undefined && object.processSession !== null)
      ? ProcessSessionStart.fromPartial(object.processSession)
      : undefined;
    message.resumeSession = (object.resumeSession !== undefined && object.resumeSession !== null)
      ? ResumeSession.fromPartial(object.resumeSession)
      : undefined;
    message.subscriptions = object.subscriptions?.map((e) => Subscription.fromPartial(e)) || [];
    return message;
  },
};

function createBaseProcessSessionStart(): ProcessSessionStart {
  return { process: undefined, lifecycle: 0, channels: [], authToken: undefined, loginBegin: undefined };
}

export const ProcessSessionStart = {
  encode(message: ProcessSessionStart, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.process !== undefined) {
      ProcessStartedRequest.encode(message.process, writer.uint32(10).fork()).ldelim();
    }
    if (message.lifecycle !== 0) {
      writer.uint32(16).int32(message.lifecycle);
    }
    for (const v of message.channels) {
      writer.uint32(26).string(v!);
    }
    if (message.authToken !== undefined) {
      writer.uint32(34).string(message.authToken);
    }
    if (message.loginBegin !== undefined) {
      LoginBeginRequest.encode(message.loginBegin, writer.uint32(42).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ProcessSessionStart {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseProcessSessionStart();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.process = ProcessStartedRequest.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.lifecycle = reader.int32() as any;
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.channels.push(reader.string());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.authToken = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.loginBegin = LoginBeginRequest.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ProcessSessionStart {
    return {
      process: isSet(object.process) ? ProcessStartedRequest.fromJSON(object.process) : undefined,
      lifecycle: isSet(object.lifecycle) ? mailboxLifecycleFromJSON(object.lifecycle) : 0,
      channels: globalThis.Array.isArray(object?.channels) ? object.channels.map((e: any) => globalThis.String(e)) : [],
      authToken: isSet(object.authToken) ? globalThis.String(object.authToken) : undefined,
      loginBegin: isSet(object.loginBegin) ? LoginBeginRequest.fromJSON(object.loginBegin) : undefined,
    };
  },

  toJSON(message: ProcessSessionStart): unknown {
    const obj: any = {};
    if (message.process !== undefined) {
      obj.process = ProcessStartedRequest.toJSON(message.process);
    }
    if (message.lifecycle !== 0) {
      obj.lifecycle = mailboxLifecycleToJSON(message.lifecycle);
    }
    if (message.channels?.length) {
      obj.channels = message.channels;
    }
    if (message.authToken !== undefined) {
      obj.authToken = message.authToken;
    }
    if (message.loginBegin !== undefined) {
      obj.loginBegin = LoginBeginRequest.toJSON(message.loginBegin);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ProcessSessionStart>, I>>(base?: I): ProcessSessionStart {
    return ProcessSessionStart.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ProcessSessionStart>, I>>(object: I): ProcessSessionStart {
    const message = createBaseProcessSessionStart();
    message.process = (object.process !== undefined && object.process !== null)
      ? ProcessStartedRequest.fromPartial(object.process)
      : undefined;
    message.lifecycle = object.lifecycle ?? 0;
    message.channels = object.channels?.map((e) => e) || [];
    message.authToken = object.authToken ?? undefined;
    message.loginBegin = (object.loginBegin !== undefined && object.loginBegin !== null)
      ? LoginBeginRequest.fromPartial(object.loginBegin)
      : undefined;
    return message;
  },
};

function createBaseResumeSession(): ResumeSession {
  return { readerKey: "", authToken: "" };
}

export const ResumeSession = {
  encode(message: ResumeSession, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.readerKey !== "") {
      writer.uint32(10).string(message.readerKey);
    }
    if (message.authToken !== "") {
      writer.uint32(18).string(message.authToken);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ResumeSession {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseResumeSession();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.readerKey = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.authToken = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ResumeSession {
    return {
      readerKey: isSet(object.readerKey) ? globalThis.String(object.readerKey) : "",
      authToken: isSet(object.authToken) ? globalThis.String(object.authToken) : "",
    };
  },

  toJSON(message: ResumeSession): unknown {
    const obj: any = {};
    if (message.readerKey !== "") {
      obj.readerKey = message.readerKey;
    }
    if (message.authToken !== "") {
      obj.authToken = message.authToken;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ResumeSession>, I>>(base?: I): ResumeSession {
    return ResumeSession.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ResumeSession>, I>>(object: I): ResumeSession {
    const message = createBaseResumeSession();
    message.readerKey = object.readerKey ?? "";
    message.authToken = object.authToken ?? "";
    return message;
  },
};

function createBaseLoginComplete(): LoginComplete {
  return { sessionId: "", signature: new Uint8Array(0) };
}

export const LoginComplete = {
  encode(message: LoginComplete, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.sessionId !== "") {
      writer.uint32(10).string(message.sessionId);
    }
    if (message.signature.length !== 0) {
      writer.uint32(18).bytes(message.signature);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LoginComplete {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLoginComplete();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.sessionId = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.signature = reader.bytes();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LoginComplete {
    return {
      sessionId: isSet(object.sessionId) ? globalThis.String(object.sessionId) : "",
      signature: isSet(object.signature) ? bytesFromBase64(object.signature) : new Uint8Array(0),
    };
  },

  toJSON(message: LoginComplete): unknown {
    const obj: any = {};
    if (message.sessionId !== "") {
      obj.sessionId = message.sessionId;
    }
    if (message.signature.length !== 0) {
      obj.signature = base64FromBytes(message.signature);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LoginComplete>, I>>(base?: I): LoginComplete {
    return LoginComplete.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LoginComplete>, I>>(object: I): LoginComplete {
    const message = createBaseLoginComplete();
    message.sessionId = object.sessionId ?? "";
    message.signature = object.signature ?? new Uint8Array(0);
    return message;
  },
};

function createBaseLoginChallenge(): LoginChallenge {
  return { sessionId: "", nonce: new Uint8Array(0) };
}

export const LoginChallenge = {
  encode(message: LoginChallenge, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.sessionId !== "") {
      writer.uint32(10).string(message.sessionId);
    }
    if (message.nonce.length !== 0) {
      writer.uint32(18).bytes(message.nonce);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LoginChallenge {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLoginChallenge();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.sessionId = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.nonce = reader.bytes();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LoginChallenge {
    return {
      sessionId: isSet(object.sessionId) ? globalThis.String(object.sessionId) : "",
      nonce: isSet(object.nonce) ? bytesFromBase64(object.nonce) : new Uint8Array(0),
    };
  },

  toJSON(message: LoginChallenge): unknown {
    const obj: any = {};
    if (message.sessionId !== "") {
      obj.sessionId = message.sessionId;
    }
    if (message.nonce.length !== 0) {
      obj.nonce = base64FromBytes(message.nonce);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LoginChallenge>, I>>(base?: I): LoginChallenge {
    return LoginChallenge.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LoginChallenge>, I>>(object: I): LoginChallenge {
    const message = createBaseLoginChallenge();
    message.sessionId = object.sessionId ?? "";
    message.nonce = object.nonce ?? new Uint8Array(0);
    return message;
  },
};

function createBaseHelloError(): HelloError {
  return { code: 0, message: "" };
}

export const HelloError = {
  encode(message: HelloError, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.code !== 0) {
      writer.uint32(8).int32(message.code);
    }
    if (message.message !== "") {
      writer.uint32(18).string(message.message);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): HelloError {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseHelloError();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.code = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.message = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): HelloError {
    return {
      code: isSet(object.code) ? helloErrorCodeFromJSON(object.code) : 0,
      message: isSet(object.message) ? globalThis.String(object.message) : "",
    };
  },

  toJSON(message: HelloError): unknown {
    const obj: any = {};
    if (message.code !== 0) {
      obj.code = helloErrorCodeToJSON(message.code);
    }
    if (message.message !== "") {
      obj.message = message.message;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<HelloError>, I>>(base?: I): HelloError {
    return HelloError.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<HelloError>, I>>(object: I): HelloError {
    const message = createBaseHelloError();
    message.code = object.code ?? 0;
    message.message = object.message ?? "";
    return message;
  },
};

function createBaseClientSessionStart(): ClientSessionStart {
  return { authToken: "", browser: undefined, process: undefined, lifecycleKind: "", channels: [] };
}

export const ClientSessionStart = {
  encode(message: ClientSessionStart, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.authToken !== "") {
      writer.uint32(66).string(message.authToken);
    }
    if (message.browser !== undefined) {
      BrowserSessionStart.encode(message.browser, writer.uint32(74).fork()).ldelim();
    }
    if (message.process !== undefined) {
      ProcessStartedRequest.encode(message.process, writer.uint32(82).fork()).ldelim();
    }
    if (message.lifecycleKind !== "") {
      writer.uint32(34).string(message.lifecycleKind);
    }
    for (const v of message.channels) {
      writer.uint32(42).string(v!);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ClientSessionStart {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseClientSessionStart();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 8:
          if (tag !== 66) {
            break;
          }

          message.authToken = reader.string();
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.browser = BrowserSessionStart.decode(reader, reader.uint32());
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.process = ProcessStartedRequest.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.lifecycleKind = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.channels.push(reader.string());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ClientSessionStart {
    return {
      authToken: isSet(object.authToken) ? globalThis.String(object.authToken) : "",
      browser: isSet(object.browser) ? BrowserSessionStart.fromJSON(object.browser) : undefined,
      process: isSet(object.process) ? ProcessStartedRequest.fromJSON(object.process) : undefined,
      lifecycleKind: isSet(object.lifecycleKind) ? globalThis.String(object.lifecycleKind) : "",
      channels: globalThis.Array.isArray(object?.channels) ? object.channels.map((e: any) => globalThis.String(e)) : [],
    };
  },

  toJSON(message: ClientSessionStart): unknown {
    const obj: any = {};
    if (message.authToken !== "") {
      obj.authToken = message.authToken;
    }
    if (message.browser !== undefined) {
      obj.browser = BrowserSessionStart.toJSON(message.browser);
    }
    if (message.process !== undefined) {
      obj.process = ProcessStartedRequest.toJSON(message.process);
    }
    if (message.lifecycleKind !== "") {
      obj.lifecycleKind = message.lifecycleKind;
    }
    if (message.channels?.length) {
      obj.channels = message.channels;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ClientSessionStart>, I>>(base?: I): ClientSessionStart {
    return ClientSessionStart.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ClientSessionStart>, I>>(object: I): ClientSessionStart {
    const message = createBaseClientSessionStart();
    message.authToken = object.authToken ?? "";
    message.browser = (object.browser !== undefined && object.browser !== null)
      ? BrowserSessionStart.fromPartial(object.browser)
      : undefined;
    message.process = (object.process !== undefined && object.process !== null)
      ? ProcessStartedRequest.fromPartial(object.process)
      : undefined;
    message.lifecycleKind = object.lifecycleKind ?? "";
    message.channels = object.channels?.map((e) => e) || [];
    return message;
  },
};

function createBaseClientSessionStarted(): ClientSessionStarted {
  return { address: "", adminKey: "", readerKey: "", channels: [], processUid: "", workerUid: "", authToken: "" };
}

export const ClientSessionStarted = {
  encode(message: ClientSessionStarted, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.address !== "") {
      writer.uint32(10).string(message.address);
    }
    if (message.adminKey !== "") {
      writer.uint32(18).string(message.adminKey);
    }
    if (message.readerKey !== "") {
      writer.uint32(26).string(message.readerKey);
    }
    for (const v of message.channels) {
      writer.uint32(34).string(v!);
    }
    if (message.processUid !== "") {
      writer.uint32(42).string(message.processUid);
    }
    if (message.workerUid !== "") {
      writer.uint32(50).string(message.workerUid);
    }
    if (message.authToken !== "") {
      writer.uint32(58).string(message.authToken);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ClientSessionStarted {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseClientSessionStarted();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.address = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.adminKey = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.readerKey = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.channels.push(reader.string());
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.processUid = reader.string();
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.workerUid = reader.string();
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.authToken = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ClientSessionStarted {
    return {
      address: isSet(object.address) ? globalThis.String(object.address) : "",
      adminKey: isSet(object.adminKey) ? globalThis.String(object.adminKey) : "",
      readerKey: isSet(object.readerKey) ? globalThis.String(object.readerKey) : "",
      channels: globalThis.Array.isArray(object?.channels) ? object.channels.map((e: any) => globalThis.String(e)) : [],
      processUid: isSet(object.processUid) ? globalThis.String(object.processUid) : "",
      workerUid: isSet(object.workerUid) ? globalThis.String(object.workerUid) : "",
      authToken: isSet(object.authToken) ? globalThis.String(object.authToken) : "",
    };
  },

  toJSON(message: ClientSessionStarted): unknown {
    const obj: any = {};
    if (message.address !== "") {
      obj.address = message.address;
    }
    if (message.adminKey !== "") {
      obj.adminKey = message.adminKey;
    }
    if (message.readerKey !== "") {
      obj.readerKey = message.readerKey;
    }
    if (message.channels?.length) {
      obj.channels = message.channels;
    }
    if (message.processUid !== "") {
      obj.processUid = message.processUid;
    }
    if (message.workerUid !== "") {
      obj.workerUid = message.workerUid;
    }
    if (message.authToken !== "") {
      obj.authToken = message.authToken;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ClientSessionStarted>, I>>(base?: I): ClientSessionStarted {
    return ClientSessionStarted.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ClientSessionStarted>, I>>(object: I): ClientSessionStarted {
    const message = createBaseClientSessionStarted();
    message.address = object.address ?? "";
    message.adminKey = object.adminKey ?? "";
    message.readerKey = object.readerKey ?? "";
    message.channels = object.channels?.map((e) => e) || [];
    message.processUid = object.processUid ?? "";
    message.workerUid = object.workerUid ?? "";
    message.authToken = object.authToken ?? "";
    return message;
  },
};

function createBaseBrowserSessionStart(): BrowserSessionStart {
  return {
    tabUid: "",
    mainName: "",
    startedAt: undefined,
    url: "",
    referer: "",
    locale: "",
    timezone: "",
    authToken: "",
    buildInfo: undefined,
    ip: "",
    userAgent: "",
    extraData: undefined,
  };
}

export const BrowserSessionStart = {
  encode(message: BrowserSessionStart, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.tabUid !== "") {
      writer.uint32(10).string(message.tabUid);
    }
    if (message.mainName !== "") {
      writer.uint32(18).string(message.mainName);
    }
    if (message.startedAt !== undefined) {
      Timestamp.encode(toTimestamp(message.startedAt), writer.uint32(26).fork()).ldelim();
    }
    if (message.url !== "") {
      writer.uint32(50).string(message.url);
    }
    if (message.referer !== "") {
      writer.uint32(58).string(message.referer);
    }
    if (message.locale !== "") {
      writer.uint32(66).string(message.locale);
    }
    if (message.timezone !== "") {
      writer.uint32(74).string(message.timezone);
    }
    if (message.authToken !== "") {
      writer.uint32(82).string(message.authToken);
    }
    if (message.buildInfo !== undefined) {
      BuildInfo.encode(message.buildInfo, writer.uint32(106).fork()).ldelim();
    }
    if (message.ip !== "") {
      writer.uint32(90).string(message.ip);
    }
    if (message.userAgent !== "") {
      writer.uint32(98).string(message.userAgent);
    }
    if (message.extraData !== undefined) {
      Struct.encode(Struct.wrap(message.extraData), writer.uint32(34).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): BrowserSessionStart {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseBrowserSessionStart();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.tabUid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.mainName = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.startedAt = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.url = reader.string();
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.referer = reader.string();
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.locale = reader.string();
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.timezone = reader.string();
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.authToken = reader.string();
          continue;
        case 13:
          if (tag !== 106) {
            break;
          }

          message.buildInfo = BuildInfo.decode(reader, reader.uint32());
          continue;
        case 11:
          if (tag !== 90) {
            break;
          }

          message.ip = reader.string();
          continue;
        case 12:
          if (tag !== 98) {
            break;
          }

          message.userAgent = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.extraData = Struct.unwrap(Struct.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): BrowserSessionStart {
    return {
      tabUid: isSet(object.tabUid) ? globalThis.String(object.tabUid) : "",
      mainName: isSet(object.mainName) ? globalThis.String(object.mainName) : "",
      startedAt: isSet(object.startedAt) ? fromJsonTimestamp(object.startedAt) : undefined,
      url: isSet(object.url) ? globalThis.String(object.url) : "",
      referer: isSet(object.referer) ? globalThis.String(object.referer) : "",
      locale: isSet(object.locale) ? globalThis.String(object.locale) : "",
      timezone: isSet(object.timezone) ? globalThis.String(object.timezone) : "",
      authToken: isSet(object.authToken) ? globalThis.String(object.authToken) : "",
      buildInfo: isSet(object.buildInfo) ? BuildInfo.fromJSON(object.buildInfo) : undefined,
      ip: isSet(object.ip) ? globalThis.String(object.ip) : "",
      userAgent: isSet(object.userAgent) ? globalThis.String(object.userAgent) : "",
      extraData: isObject(object.extraData) ? object.extraData : undefined,
    };
  },

  toJSON(message: BrowserSessionStart): unknown {
    const obj: any = {};
    if (message.tabUid !== "") {
      obj.tabUid = message.tabUid;
    }
    if (message.mainName !== "") {
      obj.mainName = message.mainName;
    }
    if (message.startedAt !== undefined) {
      obj.startedAt = message.startedAt.toISOString();
    }
    if (message.url !== "") {
      obj.url = message.url;
    }
    if (message.referer !== "") {
      obj.referer = message.referer;
    }
    if (message.locale !== "") {
      obj.locale = message.locale;
    }
    if (message.timezone !== "") {
      obj.timezone = message.timezone;
    }
    if (message.authToken !== "") {
      obj.authToken = message.authToken;
    }
    if (message.buildInfo !== undefined) {
      obj.buildInfo = BuildInfo.toJSON(message.buildInfo);
    }
    if (message.ip !== "") {
      obj.ip = message.ip;
    }
    if (message.userAgent !== "") {
      obj.userAgent = message.userAgent;
    }
    if (message.extraData !== undefined) {
      obj.extraData = message.extraData;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<BrowserSessionStart>, I>>(base?: I): BrowserSessionStart {
    return BrowserSessionStart.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<BrowserSessionStart>, I>>(object: I): BrowserSessionStart {
    const message = createBaseBrowserSessionStart();
    message.tabUid = object.tabUid ?? "";
    message.mainName = object.mainName ?? "";
    message.startedAt = object.startedAt ?? undefined;
    message.url = object.url ?? "";
    message.referer = object.referer ?? "";
    message.locale = object.locale ?? "";
    message.timezone = object.timezone ?? "";
    message.authToken = object.authToken ?? "";
    message.buildInfo = (object.buildInfo !== undefined && object.buildInfo !== null)
      ? BuildInfo.fromPartial(object.buildInfo)
      : undefined;
    message.ip = object.ip ?? "";
    message.userAgent = object.userAgent ?? "";
    message.extraData = object.extraData ?? undefined;
    return message;
  },
};

function createBasePublishToContinuumCentral(): PublishToContinuumCentral {
  return { workerUid: "", messageFromRunnerBytes: new Uint8Array(0) };
}

export const PublishToContinuumCentral = {
  encode(message: PublishToContinuumCentral, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.workerUid !== "") {
      writer.uint32(10).string(message.workerUid);
    }
    if (message.messageFromRunnerBytes.length !== 0) {
      writer.uint32(18).bytes(message.messageFromRunnerBytes);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PublishToContinuumCentral {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePublishToContinuumCentral();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.workerUid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.messageFromRunnerBytes = reader.bytes();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PublishToContinuumCentral {
    return {
      workerUid: isSet(object.workerUid) ? globalThis.String(object.workerUid) : "",
      messageFromRunnerBytes: isSet(object.messageFromRunnerBytes)
        ? bytesFromBase64(object.messageFromRunnerBytes)
        : new Uint8Array(0),
    };
  },

  toJSON(message: PublishToContinuumCentral): unknown {
    const obj: any = {};
    if (message.workerUid !== "") {
      obj.workerUid = message.workerUid;
    }
    if (message.messageFromRunnerBytes.length !== 0) {
      obj.messageFromRunnerBytes = base64FromBytes(message.messageFromRunnerBytes);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PublishToContinuumCentral>, I>>(base?: I): PublishToContinuumCentral {
    return PublishToContinuumCentral.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PublishToContinuumCentral>, I>>(object: I): PublishToContinuumCentral {
    const message = createBasePublishToContinuumCentral();
    message.workerUid = object.workerUid ?? "";
    message.messageFromRunnerBytes = object.messageFromRunnerBytes ?? new Uint8Array(0);
    return message;
  },
};

function createBaseNotification(): Notification {
  return { message: "" };
}

export const Notification = {
  encode(message: Notification, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.message !== "") {
      writer.uint32(10).string(message.message);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Notification {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseNotification();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.message = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Notification {
    return { message: isSet(object.message) ? globalThis.String(object.message) : "" };
  },

  toJSON(message: Notification): unknown {
    const obj: any = {};
    if (message.message !== "") {
      obj.message = message.message;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Notification>, I>>(base?: I): Notification {
    return Notification.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Notification>, I>>(object: I): Notification {
    const message = createBaseNotification();
    message.message = object.message ?? "";
    return message;
  },
};

function createBaseMessageToClient(): MessageToClient {
  return {
    messageEnvelope: undefined,
    sendMessageResponse: undefined,
    ping: undefined,
    pong: undefined,
    notification: undefined,
    subscribeResponse: undefined,
    clientSessionStarted: undefined,
    loginChallenge: undefined,
    helloError: undefined,
    devLoopResponse: undefined,
  };
}

export const MessageToClient = {
  encode(message: MessageToClient, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.messageEnvelope !== undefined) {
      MessageEnvelope.encode(message.messageEnvelope, writer.uint32(10).fork()).ldelim();
    }
    if (message.sendMessageResponse !== undefined) {
      SendMessageResponse.encode(message.sendMessageResponse, writer.uint32(18).fork()).ldelim();
    }
    if (message.ping !== undefined) {
      Ping.encode(message.ping, writer.uint32(26).fork()).ldelim();
    }
    if (message.pong !== undefined) {
      Pong.encode(message.pong, writer.uint32(34).fork()).ldelim();
    }
    if (message.notification !== undefined) {
      Notification.encode(message.notification, writer.uint32(42).fork()).ldelim();
    }
    if (message.subscribeResponse !== undefined) {
      SubscribeResponse.encode(message.subscribeResponse, writer.uint32(50).fork()).ldelim();
    }
    if (message.clientSessionStarted !== undefined) {
      ClientSessionStarted.encode(message.clientSessionStarted, writer.uint32(58).fork()).ldelim();
    }
    if (message.loginChallenge !== undefined) {
      LoginChallenge.encode(message.loginChallenge, writer.uint32(66).fork()).ldelim();
    }
    if (message.helloError !== undefined) {
      HelloError.encode(message.helloError, writer.uint32(74).fork()).ldelim();
    }
    if (message.devLoopResponse !== undefined) {
      DevLoopResponse.encode(message.devLoopResponse, writer.uint32(82).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MessageToClient {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMessageToClient();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.messageEnvelope = MessageEnvelope.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.sendMessageResponse = SendMessageResponse.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.ping = Ping.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.pong = Pong.decode(reader, reader.uint32());
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.notification = Notification.decode(reader, reader.uint32());
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.subscribeResponse = SubscribeResponse.decode(reader, reader.uint32());
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.clientSessionStarted = ClientSessionStarted.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.loginChallenge = LoginChallenge.decode(reader, reader.uint32());
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.helloError = HelloError.decode(reader, reader.uint32());
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.devLoopResponse = DevLoopResponse.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): MessageToClient {
    return {
      messageEnvelope: isSet(object.messageEnvelope) ? MessageEnvelope.fromJSON(object.messageEnvelope) : undefined,
      sendMessageResponse: isSet(object.sendMessageResponse)
        ? SendMessageResponse.fromJSON(object.sendMessageResponse)
        : undefined,
      ping: isSet(object.ping) ? Ping.fromJSON(object.ping) : undefined,
      pong: isSet(object.pong) ? Pong.fromJSON(object.pong) : undefined,
      notification: isSet(object.notification) ? Notification.fromJSON(object.notification) : undefined,
      subscribeResponse: isSet(object.subscribeResponse)
        ? SubscribeResponse.fromJSON(object.subscribeResponse)
        : undefined,
      clientSessionStarted: isSet(object.clientSessionStarted)
        ? ClientSessionStarted.fromJSON(object.clientSessionStarted)
        : undefined,
      loginChallenge: isSet(object.loginChallenge) ? LoginChallenge.fromJSON(object.loginChallenge) : undefined,
      helloError: isSet(object.helloError) ? HelloError.fromJSON(object.helloError) : undefined,
      devLoopResponse: isSet(object.devLoopResponse) ? DevLoopResponse.fromJSON(object.devLoopResponse) : undefined,
    };
  },

  toJSON(message: MessageToClient): unknown {
    const obj: any = {};
    if (message.messageEnvelope !== undefined) {
      obj.messageEnvelope = MessageEnvelope.toJSON(message.messageEnvelope);
    }
    if (message.sendMessageResponse !== undefined) {
      obj.sendMessageResponse = SendMessageResponse.toJSON(message.sendMessageResponse);
    }
    if (message.ping !== undefined) {
      obj.ping = Ping.toJSON(message.ping);
    }
    if (message.pong !== undefined) {
      obj.pong = Pong.toJSON(message.pong);
    }
    if (message.notification !== undefined) {
      obj.notification = Notification.toJSON(message.notification);
    }
    if (message.subscribeResponse !== undefined) {
      obj.subscribeResponse = SubscribeResponse.toJSON(message.subscribeResponse);
    }
    if (message.clientSessionStarted !== undefined) {
      obj.clientSessionStarted = ClientSessionStarted.toJSON(message.clientSessionStarted);
    }
    if (message.loginChallenge !== undefined) {
      obj.loginChallenge = LoginChallenge.toJSON(message.loginChallenge);
    }
    if (message.helloError !== undefined) {
      obj.helloError = HelloError.toJSON(message.helloError);
    }
    if (message.devLoopResponse !== undefined) {
      obj.devLoopResponse = DevLoopResponse.toJSON(message.devLoopResponse);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<MessageToClient>, I>>(base?: I): MessageToClient {
    return MessageToClient.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<MessageToClient>, I>>(object: I): MessageToClient {
    const message = createBaseMessageToClient();
    message.messageEnvelope = (object.messageEnvelope !== undefined && object.messageEnvelope !== null)
      ? MessageEnvelope.fromPartial(object.messageEnvelope)
      : undefined;
    message.sendMessageResponse = (object.sendMessageResponse !== undefined && object.sendMessageResponse !== null)
      ? SendMessageResponse.fromPartial(object.sendMessageResponse)
      : undefined;
    message.ping = (object.ping !== undefined && object.ping !== null) ? Ping.fromPartial(object.ping) : undefined;
    message.pong = (object.pong !== undefined && object.pong !== null) ? Pong.fromPartial(object.pong) : undefined;
    message.notification = (object.notification !== undefined && object.notification !== null)
      ? Notification.fromPartial(object.notification)
      : undefined;
    message.subscribeResponse = (object.subscribeResponse !== undefined && object.subscribeResponse !== null)
      ? SubscribeResponse.fromPartial(object.subscribeResponse)
      : undefined;
    message.clientSessionStarted = (object.clientSessionStarted !== undefined && object.clientSessionStarted !== null)
      ? ClientSessionStarted.fromPartial(object.clientSessionStarted)
      : undefined;
    message.loginChallenge = (object.loginChallenge !== undefined && object.loginChallenge !== null)
      ? LoginChallenge.fromPartial(object.loginChallenge)
      : undefined;
    message.helloError = (object.helloError !== undefined && object.helloError !== null)
      ? HelloError.fromPartial(object.helloError)
      : undefined;
    message.devLoopResponse = (object.devLoopResponse !== undefined && object.devLoopResponse !== null)
      ? DevLoopResponse.fromPartial(object.devLoopResponse)
      : undefined;
    return message;
  },
};

function createBasePing(): Ping {
  return { payload: new Uint8Array(0) };
}

export const Ping = {
  encode(message: Ping, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.payload.length !== 0) {
      writer.uint32(10).bytes(message.payload);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Ping {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePing();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.payload = reader.bytes();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Ping {
    return { payload: isSet(object.payload) ? bytesFromBase64(object.payload) : new Uint8Array(0) };
  },

  toJSON(message: Ping): unknown {
    const obj: any = {};
    if (message.payload.length !== 0) {
      obj.payload = base64FromBytes(message.payload);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Ping>, I>>(base?: I): Ping {
    return Ping.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Ping>, I>>(object: I): Ping {
    const message = createBasePing();
    message.payload = object.payload ?? new Uint8Array(0);
    return message;
  },
};

function createBasePong(): Pong {
  return { payload: new Uint8Array(0) };
}

export const Pong = {
  encode(message: Pong, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.payload.length !== 0) {
      writer.uint32(10).bytes(message.payload);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Pong {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePong();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.payload = reader.bytes();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Pong {
    return { payload: isSet(object.payload) ? bytesFromBase64(object.payload) : new Uint8Array(0) };
  },

  toJSON(message: Pong): unknown {
    const obj: any = {};
    if (message.payload.length !== 0) {
      obj.payload = base64FromBytes(message.payload);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Pong>, I>>(base?: I): Pong {
    return Pong.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Pong>, I>>(object: I): Pong {
    const message = createBasePong();
    message.payload = object.payload ?? new Uint8Array(0);
    return message;
  },
};

function createBaseMessageHeader(): MessageHeader {
  return { sender: "", contentType: 0, rpcHeader: undefined, senderSequence: "0", extraHeaders: [] };
}

export const MessageHeader = {
  encode(message: MessageHeader, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.sender !== "") {
      writer.uint32(10).string(message.sender);
    }
    if (message.contentType !== 0) {
      writer.uint32(16).int32(message.contentType);
    }
    if (message.rpcHeader !== undefined) {
      RpcHeader.encode(message.rpcHeader, writer.uint32(26).fork()).ldelim();
    }
    if (message.senderSequence !== "0") {
      writer.uint32(32).int64(message.senderSequence);
    }
    for (const v of message.extraHeaders) {
      KeyValPair.encode(v!, writer.uint32(1602).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MessageHeader {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMessageHeader();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.sender = reader.string();
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.contentType = reader.int32() as any;
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.rpcHeader = RpcHeader.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.senderSequence = longToString(reader.int64() as Long);
          continue;
        case 200:
          if (tag !== 1602) {
            break;
          }

          message.extraHeaders.push(KeyValPair.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): MessageHeader {
    return {
      sender: isSet(object.sender) ? globalThis.String(object.sender) : "",
      contentType: isSet(object.contentType) ? contentTypeFromJSON(object.contentType) : 0,
      rpcHeader: isSet(object.rpcHeader) ? RpcHeader.fromJSON(object.rpcHeader) : undefined,
      senderSequence: isSet(object.senderSequence) ? globalThis.String(object.senderSequence) : "0",
      extraHeaders: globalThis.Array.isArray(object?.extraHeaders)
        ? object.extraHeaders.map((e: any) => KeyValPair.fromJSON(e))
        : [],
    };
  },

  toJSON(message: MessageHeader): unknown {
    const obj: any = {};
    if (message.sender !== "") {
      obj.sender = message.sender;
    }
    if (message.contentType !== 0) {
      obj.contentType = contentTypeToJSON(message.contentType);
    }
    if (message.rpcHeader !== undefined) {
      obj.rpcHeader = RpcHeader.toJSON(message.rpcHeader);
    }
    if (message.senderSequence !== "0") {
      obj.senderSequence = message.senderSequence;
    }
    if (message.extraHeaders?.length) {
      obj.extraHeaders = message.extraHeaders.map((e) => KeyValPair.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<MessageHeader>, I>>(base?: I): MessageHeader {
    return MessageHeader.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<MessageHeader>, I>>(object: I): MessageHeader {
    const message = createBaseMessageHeader();
    message.sender = object.sender ?? "";
    message.contentType = object.contentType ?? 0;
    message.rpcHeader = (object.rpcHeader !== undefined && object.rpcHeader !== null)
      ? RpcHeader.fromPartial(object.rpcHeader)
      : undefined;
    message.senderSequence = object.senderSequence ?? "0";
    message.extraHeaders = object.extraHeaders?.map((e) => KeyValPair.fromPartial(e)) || [];
    return message;
  },
};

function createBaseSenderEnvelope(): SenderEnvelope {
  return { created: "0" };
}

export const SenderEnvelope = {
  encode(message: SenderEnvelope, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.created !== "0") {
      writer.uint32(8).int64(message.created);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SenderEnvelope {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSenderEnvelope();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.created = longToString(reader.int64() as Long);
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SenderEnvelope {
    return { created: isSet(object.created) ? globalThis.String(object.created) : "0" };
  },

  toJSON(message: SenderEnvelope): unknown {
    const obj: any = {};
    if (message.created !== "0") {
      obj.created = message.created;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SenderEnvelope>, I>>(base?: I): SenderEnvelope {
    return SenderEnvelope.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SenderEnvelope>, I>>(object: I): SenderEnvelope {
    const message = createBaseSenderEnvelope();
    message.created = object.created ?? "0";
    return message;
  },
};

function createBaseServerEnvelope(): ServerEnvelope {
  return { sequence: "0", created: "0", channel: "", subscriptionId: "" };
}

export const ServerEnvelope = {
  encode(message: ServerEnvelope, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.sequence !== "0") {
      writer.uint32(8).uint64(message.sequence);
    }
    if (message.created !== "0") {
      writer.uint32(16).int64(message.created);
    }
    if (message.channel !== "") {
      writer.uint32(26).string(message.channel);
    }
    if (message.subscriptionId !== "") {
      writer.uint32(34).string(message.subscriptionId);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ServerEnvelope {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseServerEnvelope();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.sequence = longToString(reader.uint64() as Long);
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.created = longToString(reader.int64() as Long);
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.channel = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.subscriptionId = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ServerEnvelope {
    return {
      sequence: isSet(object.sequence) ? globalThis.String(object.sequence) : "0",
      created: isSet(object.created) ? globalThis.String(object.created) : "0",
      channel: isSet(object.channel) ? globalThis.String(object.channel) : "",
      subscriptionId: isSet(object.subscriptionId) ? globalThis.String(object.subscriptionId) : "",
    };
  },

  toJSON(message: ServerEnvelope): unknown {
    const obj: any = {};
    if (message.sequence !== "0") {
      obj.sequence = message.sequence;
    }
    if (message.created !== "0") {
      obj.created = message.created;
    }
    if (message.channel !== "") {
      obj.channel = message.channel;
    }
    if (message.subscriptionId !== "") {
      obj.subscriptionId = message.subscriptionId;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ServerEnvelope>, I>>(base?: I): ServerEnvelope {
    return ServerEnvelope.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ServerEnvelope>, I>>(object: I): ServerEnvelope {
    const message = createBaseServerEnvelope();
    message.sequence = object.sequence ?? "0";
    message.created = object.created ?? "0";
    message.channel = object.channel ?? "";
    message.subscriptionId = object.subscriptionId ?? "";
    return message;
  },
};

function createBaseKeyValPair(): KeyValPair {
  return { key: "", val: "" };
}

export const KeyValPair = {
  encode(message: KeyValPair, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.val !== "") {
      writer.uint32(18).string(message.val);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): KeyValPair {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseKeyValPair();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.val = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): KeyValPair {
    return {
      key: isSet(object.key) ? globalThis.String(object.key) : "",
      val: isSet(object.val) ? globalThis.String(object.val) : "",
    };
  },

  toJSON(message: KeyValPair): unknown {
    const obj: any = {};
    if (message.key !== "") {
      obj.key = message.key;
    }
    if (message.val !== "") {
      obj.val = message.val;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<KeyValPair>, I>>(base?: I): KeyValPair {
    return KeyValPair.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<KeyValPair>, I>>(object: I): KeyValPair {
    const message = createBaseKeyValPair();
    message.key = object.key ?? "";
    message.val = object.val ?? "";
    return message;
  },
};

function createBaseRpcHeader(): RpcHeader {
  return {
    correlationId: "",
    endPoint: "",
    frameType: 0,
    errorInfo: undefined,
    callKind: 0,
    frameSeq: "0",
    cancelReason: "",
  };
}

export const RpcHeader = {
  encode(message: RpcHeader, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.correlationId !== "") {
      writer.uint32(18).string(message.correlationId);
    }
    if (message.endPoint !== "") {
      writer.uint32(26).string(message.endPoint);
    }
    if (message.frameType !== 0) {
      writer.uint32(32).int32(message.frameType);
    }
    if (message.errorInfo !== undefined) {
      RpcErrorInfo.encode(message.errorInfo, writer.uint32(42).fork()).ldelim();
    }
    if (message.callKind !== 0) {
      writer.uint32(48).int32(message.callKind);
    }
    if (message.frameSeq !== "0") {
      writer.uint32(56).uint64(message.frameSeq);
    }
    if (message.cancelReason !== "") {
      writer.uint32(66).string(message.cancelReason);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RpcHeader {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRpcHeader();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2:
          if (tag !== 18) {
            break;
          }

          message.correlationId = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.endPoint = reader.string();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.frameType = reader.int32() as any;
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.errorInfo = RpcErrorInfo.decode(reader, reader.uint32());
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.callKind = reader.int32() as any;
          continue;
        case 7:
          if (tag !== 56) {
            break;
          }

          message.frameSeq = longToString(reader.uint64() as Long);
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.cancelReason = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RpcHeader {
    return {
      correlationId: isSet(object.correlationId) ? globalThis.String(object.correlationId) : "",
      endPoint: isSet(object.endPoint) ? globalThis.String(object.endPoint) : "",
      frameType: isSet(object.frameType) ? rpcFrameTypeFromJSON(object.frameType) : 0,
      errorInfo: isSet(object.errorInfo) ? RpcErrorInfo.fromJSON(object.errorInfo) : undefined,
      callKind: isSet(object.callKind) ? rpcCallKindFromJSON(object.callKind) : 0,
      frameSeq: isSet(object.frameSeq) ? globalThis.String(object.frameSeq) : "0",
      cancelReason: isSet(object.cancelReason) ? globalThis.String(object.cancelReason) : "",
    };
  },

  toJSON(message: RpcHeader): unknown {
    const obj: any = {};
    if (message.correlationId !== "") {
      obj.correlationId = message.correlationId;
    }
    if (message.endPoint !== "") {
      obj.endPoint = message.endPoint;
    }
    if (message.frameType !== 0) {
      obj.frameType = rpcFrameTypeToJSON(message.frameType);
    }
    if (message.errorInfo !== undefined) {
      obj.errorInfo = RpcErrorInfo.toJSON(message.errorInfo);
    }
    if (message.callKind !== 0) {
      obj.callKind = rpcCallKindToJSON(message.callKind);
    }
    if (message.frameSeq !== "0") {
      obj.frameSeq = message.frameSeq;
    }
    if (message.cancelReason !== "") {
      obj.cancelReason = message.cancelReason;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RpcHeader>, I>>(base?: I): RpcHeader {
    return RpcHeader.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RpcHeader>, I>>(object: I): RpcHeader {
    const message = createBaseRpcHeader();
    message.correlationId = object.correlationId ?? "";
    message.endPoint = object.endPoint ?? "";
    message.frameType = object.frameType ?? 0;
    message.errorInfo = (object.errorInfo !== undefined && object.errorInfo !== null)
      ? RpcErrorInfo.fromPartial(object.errorInfo)
      : undefined;
    message.callKind = object.callKind ?? 0;
    message.frameSeq = object.frameSeq ?? "0";
    message.cancelReason = object.cancelReason ?? "";
    return message;
  },
};

function createBaseErrorLocation(): ErrorLocation {
  return { file: "", line: 0, function: "" };
}

export const ErrorLocation = {
  encode(message: ErrorLocation, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.file !== "") {
      writer.uint32(10).string(message.file);
    }
    if (message.line !== 0) {
      writer.uint32(16).int32(message.line);
    }
    if (message.function !== "") {
      writer.uint32(26).string(message.function);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ErrorLocation {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseErrorLocation();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.file = reader.string();
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.line = reader.int32();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.function = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ErrorLocation {
    return {
      file: isSet(object.file) ? globalThis.String(object.file) : "",
      line: isSet(object.line) ? globalThis.Number(object.line) : 0,
      function: isSet(object.function) ? globalThis.String(object.function) : "",
    };
  },

  toJSON(message: ErrorLocation): unknown {
    const obj: any = {};
    if (message.file !== "") {
      obj.file = message.file;
    }
    if (message.line !== 0) {
      obj.line = Math.round(message.line);
    }
    if (message.function !== "") {
      obj.function = message.function;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ErrorLocation>, I>>(base?: I): ErrorLocation {
    return ErrorLocation.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ErrorLocation>, I>>(object: I): ErrorLocation {
    const message = createBaseErrorLocation();
    message.file = object.file ?? "";
    message.line = object.line ?? 0;
    message.function = object.function ?? "";
    return message;
  },
};

function createBaseErrorFrame(): ErrorFrame {
  return {
    category: "",
    kind: "",
    message: "",
    retryable: false,
    details: undefined,
    component: "",
    location: undefined,
    timestamp: undefined,
  };
}

export const ErrorFrame = {
  encode(message: ErrorFrame, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.category !== "") {
      writer.uint32(10).string(message.category);
    }
    if (message.kind !== "") {
      writer.uint32(18).string(message.kind);
    }
    if (message.message !== "") {
      writer.uint32(26).string(message.message);
    }
    if (message.retryable === true) {
      writer.uint32(32).bool(message.retryable);
    }
    if (message.details !== undefined) {
      Struct.encode(Struct.wrap(message.details), writer.uint32(42).fork()).ldelim();
    }
    if (message.component !== "") {
      writer.uint32(50).string(message.component);
    }
    if (message.location !== undefined) {
      ErrorLocation.encode(message.location, writer.uint32(58).fork()).ldelim();
    }
    if (message.timestamp !== undefined) {
      Timestamp.encode(toTimestamp(message.timestamp), writer.uint32(66).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ErrorFrame {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseErrorFrame();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.category = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.kind = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.message = reader.string();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.retryable = reader.bool();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.details = Struct.unwrap(Struct.decode(reader, reader.uint32()));
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.component = reader.string();
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.location = ErrorLocation.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.timestamp = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ErrorFrame {
    return {
      category: isSet(object.category) ? globalThis.String(object.category) : "",
      kind: isSet(object.kind) ? globalThis.String(object.kind) : "",
      message: isSet(object.message) ? globalThis.String(object.message) : "",
      retryable: isSet(object.retryable) ? globalThis.Boolean(object.retryable) : false,
      details: isObject(object.details) ? object.details : undefined,
      component: isSet(object.component) ? globalThis.String(object.component) : "",
      location: isSet(object.location) ? ErrorLocation.fromJSON(object.location) : undefined,
      timestamp: isSet(object.timestamp) ? fromJsonTimestamp(object.timestamp) : undefined,
    };
  },

  toJSON(message: ErrorFrame): unknown {
    const obj: any = {};
    if (message.category !== "") {
      obj.category = message.category;
    }
    if (message.kind !== "") {
      obj.kind = message.kind;
    }
    if (message.message !== "") {
      obj.message = message.message;
    }
    if (message.retryable === true) {
      obj.retryable = message.retryable;
    }
    if (message.details !== undefined) {
      obj.details = message.details;
    }
    if (message.component !== "") {
      obj.component = message.component;
    }
    if (message.location !== undefined) {
      obj.location = ErrorLocation.toJSON(message.location);
    }
    if (message.timestamp !== undefined) {
      obj.timestamp = message.timestamp.toISOString();
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ErrorFrame>, I>>(base?: I): ErrorFrame {
    return ErrorFrame.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ErrorFrame>, I>>(object: I): ErrorFrame {
    const message = createBaseErrorFrame();
    message.category = object.category ?? "";
    message.kind = object.kind ?? "";
    message.message = object.message ?? "";
    message.retryable = object.retryable ?? false;
    message.details = object.details ?? undefined;
    message.component = object.component ?? "";
    message.location = (object.location !== undefined && object.location !== null)
      ? ErrorLocation.fromPartial(object.location)
      : undefined;
    message.timestamp = object.timestamp ?? undefined;
    return message;
  },
};

function createBaseErrorNode(): ErrorNode {
  return { frame: undefined, causes: [] };
}

export const ErrorNode = {
  encode(message: ErrorNode, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.frame !== undefined) {
      ErrorFrame.encode(message.frame, writer.uint32(10).fork()).ldelim();
    }
    for (const v of message.causes) {
      ErrorNode.encode(v!, writer.uint32(18).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ErrorNode {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseErrorNode();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.frame = ErrorFrame.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.causes.push(ErrorNode.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ErrorNode {
    return {
      frame: isSet(object.frame) ? ErrorFrame.fromJSON(object.frame) : undefined,
      causes: globalThis.Array.isArray(object?.causes) ? object.causes.map((e: any) => ErrorNode.fromJSON(e)) : [],
    };
  },

  toJSON(message: ErrorNode): unknown {
    const obj: any = {};
    if (message.frame !== undefined) {
      obj.frame = ErrorFrame.toJSON(message.frame);
    }
    if (message.causes?.length) {
      obj.causes = message.causes.map((e) => ErrorNode.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ErrorNode>, I>>(base?: I): ErrorNode {
    return ErrorNode.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ErrorNode>, I>>(object: I): ErrorNode {
    const message = createBaseErrorNode();
    message.frame = (object.frame !== undefined && object.frame !== null)
      ? ErrorFrame.fromPartial(object.frame)
      : undefined;
    message.causes = object.causes?.map((e) => ErrorNode.fromPartial(e)) || [];
    return message;
  },
};

function createBaseRpcErrorInfo(): RpcErrorInfo {
  return { errorCode: 0, message: "", stackTrace: "", errorNode: undefined };
}

export const RpcErrorInfo = {
  encode(message: RpcErrorInfo, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.errorCode !== 0) {
      writer.uint32(8).uint32(message.errorCode);
    }
    if (message.message !== "") {
      writer.uint32(18).string(message.message);
    }
    if (message.stackTrace !== "") {
      writer.uint32(26).string(message.stackTrace);
    }
    if (message.errorNode !== undefined) {
      ErrorNode.encode(message.errorNode, writer.uint32(34).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RpcErrorInfo {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRpcErrorInfo();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.errorCode = reader.uint32();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.message = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.stackTrace = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.errorNode = ErrorNode.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RpcErrorInfo {
    return {
      errorCode: isSet(object.errorCode) ? globalThis.Number(object.errorCode) : 0,
      message: isSet(object.message) ? globalThis.String(object.message) : "",
      stackTrace: isSet(object.stackTrace) ? globalThis.String(object.stackTrace) : "",
      errorNode: isSet(object.errorNode) ? ErrorNode.fromJSON(object.errorNode) : undefined,
    };
  },

  toJSON(message: RpcErrorInfo): unknown {
    const obj: any = {};
    if (message.errorCode !== 0) {
      obj.errorCode = Math.round(message.errorCode);
    }
    if (message.message !== "") {
      obj.message = message.message;
    }
    if (message.stackTrace !== "") {
      obj.stackTrace = message.stackTrace;
    }
    if (message.errorNode !== undefined) {
      obj.errorNode = ErrorNode.toJSON(message.errorNode);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RpcErrorInfo>, I>>(base?: I): RpcErrorInfo {
    return RpcErrorInfo.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RpcErrorInfo>, I>>(object: I): RpcErrorInfo {
    const message = createBaseRpcErrorInfo();
    message.errorCode = object.errorCode ?? 0;
    message.message = object.message ?? "";
    message.stackTrace = object.stackTrace ?? "";
    message.errorNode = (object.errorNode !== undefined && object.errorNode !== null)
      ? ErrorNode.fromPartial(object.errorNode)
      : undefined;
    return message;
  },
};

function createBaseMessage(): Message {
  return {
    header: undefined,
    senderEnvelope: undefined,
    serverEnvelope: undefined,
    data: new Uint8Array(0),
    dataRef: "",
  };
}

export const Message = {
  encode(message: Message, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.header !== undefined) {
      MessageHeader.encode(message.header, writer.uint32(10).fork()).ldelim();
    }
    if (message.senderEnvelope !== undefined) {
      SenderEnvelope.encode(message.senderEnvelope, writer.uint32(18).fork()).ldelim();
    }
    if (message.serverEnvelope !== undefined) {
      ServerEnvelope.encode(message.serverEnvelope, writer.uint32(26).fork()).ldelim();
    }
    if (message.data.length !== 0) {
      writer.uint32(34).bytes(message.data);
    }
    if (message.dataRef !== "") {
      writer.uint32(42).string(message.dataRef);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Message {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMessage();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.header = MessageHeader.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.senderEnvelope = SenderEnvelope.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.serverEnvelope = ServerEnvelope.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.data = reader.bytes();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.dataRef = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Message {
    return {
      header: isSet(object.header) ? MessageHeader.fromJSON(object.header) : undefined,
      senderEnvelope: isSet(object.senderEnvelope) ? SenderEnvelope.fromJSON(object.senderEnvelope) : undefined,
      serverEnvelope: isSet(object.serverEnvelope) ? ServerEnvelope.fromJSON(object.serverEnvelope) : undefined,
      data: isSet(object.data) ? bytesFromBase64(object.data) : new Uint8Array(0),
      dataRef: isSet(object.dataRef) ? globalThis.String(object.dataRef) : "",
    };
  },

  toJSON(message: Message): unknown {
    const obj: any = {};
    if (message.header !== undefined) {
      obj.header = MessageHeader.toJSON(message.header);
    }
    if (message.senderEnvelope !== undefined) {
      obj.senderEnvelope = SenderEnvelope.toJSON(message.senderEnvelope);
    }
    if (message.serverEnvelope !== undefined) {
      obj.serverEnvelope = ServerEnvelope.toJSON(message.serverEnvelope);
    }
    if (message.data.length !== 0) {
      obj.data = base64FromBytes(message.data);
    }
    if (message.dataRef !== "") {
      obj.dataRef = message.dataRef;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Message>, I>>(base?: I): Message {
    return Message.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Message>, I>>(object: I): Message {
    const message = createBaseMessage();
    message.header = (object.header !== undefined && object.header !== null)
      ? MessageHeader.fromPartial(object.header)
      : undefined;
    message.senderEnvelope = (object.senderEnvelope !== undefined && object.senderEnvelope !== null)
      ? SenderEnvelope.fromPartial(object.senderEnvelope)
      : undefined;
    message.serverEnvelope = (object.serverEnvelope !== undefined && object.serverEnvelope !== null)
      ? ServerEnvelope.fromPartial(object.serverEnvelope)
      : undefined;
    message.data = object.data ?? new Uint8Array(0);
    message.dataRef = object.dataRef ?? "";
    return message;
  },
};

function createBaseMessageEnvelope(): MessageEnvelope {
  return { messageBytes: new Uint8Array(0), serverEnvelope: undefined };
}

export const MessageEnvelope = {
  encode(message: MessageEnvelope, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.messageBytes.length !== 0) {
      writer.uint32(10).bytes(message.messageBytes);
    }
    if (message.serverEnvelope !== undefined) {
      ServerEnvelope.encode(message.serverEnvelope, writer.uint32(18).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MessageEnvelope {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMessageEnvelope();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.messageBytes = reader.bytes();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.serverEnvelope = ServerEnvelope.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): MessageEnvelope {
    return {
      messageBytes: isSet(object.messageBytes) ? bytesFromBase64(object.messageBytes) : new Uint8Array(0),
      serverEnvelope: isSet(object.serverEnvelope) ? ServerEnvelope.fromJSON(object.serverEnvelope) : undefined,
    };
  },

  toJSON(message: MessageEnvelope): unknown {
    const obj: any = {};
    if (message.messageBytes.length !== 0) {
      obj.messageBytes = base64FromBytes(message.messageBytes);
    }
    if (message.serverEnvelope !== undefined) {
      obj.serverEnvelope = ServerEnvelope.toJSON(message.serverEnvelope);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<MessageEnvelope>, I>>(base?: I): MessageEnvelope {
    return MessageEnvelope.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<MessageEnvelope>, I>>(object: I): MessageEnvelope {
    const message = createBaseMessageEnvelope();
    message.messageBytes = object.messageBytes ?? new Uint8Array(0);
    message.serverEnvelope = (object.serverEnvelope !== undefined && object.serverEnvelope !== null)
      ? ServerEnvelope.fromPartial(object.serverEnvelope)
      : undefined;
    return message;
  },
};

function createBaseSendMessageRequest(): SendMessageRequest {
  return { to: [], message: undefined, channel: "", idempotentId: "" };
}

export const SendMessageRequest = {
  encode(message: SendMessageRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    for (const v of message.to) {
      writer.uint32(10).string(v!);
    }
    if (message.message !== undefined) {
      Message.encode(message.message, writer.uint32(18).fork()).ldelim();
    }
    if (message.channel !== "") {
      writer.uint32(26).string(message.channel);
    }
    if (message.idempotentId !== "") {
      writer.uint32(34).string(message.idempotentId);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SendMessageRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSendMessageRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.to.push(reader.string());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.message = Message.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.channel = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.idempotentId = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SendMessageRequest {
    return {
      to: globalThis.Array.isArray(object?.to) ? object.to.map((e: any) => globalThis.String(e)) : [],
      message: isSet(object.message) ? Message.fromJSON(object.message) : undefined,
      channel: isSet(object.channel) ? globalThis.String(object.channel) : "",
      idempotentId: isSet(object.idempotentId) ? globalThis.String(object.idempotentId) : "",
    };
  },

  toJSON(message: SendMessageRequest): unknown {
    const obj: any = {};
    if (message.to?.length) {
      obj.to = message.to;
    }
    if (message.message !== undefined) {
      obj.message = Message.toJSON(message.message);
    }
    if (message.channel !== "") {
      obj.channel = message.channel;
    }
    if (message.idempotentId !== "") {
      obj.idempotentId = message.idempotentId;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SendMessageRequest>, I>>(base?: I): SendMessageRequest {
    return SendMessageRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SendMessageRequest>, I>>(object: I): SendMessageRequest {
    const message = createBaseSendMessageRequest();
    message.to = object.to?.map((e) => e) || [];
    message.message = (object.message !== undefined && object.message !== null)
      ? Message.fromPartial(object.message)
      : undefined;
    message.channel = object.channel ?? "";
    message.idempotentId = object.idempotentId ?? "";
    return message;
  },
};

function createBaseSendMessageResponse(): SendMessageResponse {
  return { errors: [], duplicates: [], idempotentId: "", correlationId: "" };
}

export const SendMessageResponse = {
  encode(message: SendMessageResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    for (const v of message.errors) {
      SendMessageError.encode(v!, writer.uint32(10).fork()).ldelim();
    }
    for (const v of message.duplicates) {
      writer.uint32(18).string(v!);
    }
    if (message.idempotentId !== "") {
      writer.uint32(26).string(message.idempotentId);
    }
    if (message.correlationId !== "") {
      writer.uint32(34).string(message.correlationId);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SendMessageResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSendMessageResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.errors.push(SendMessageError.decode(reader, reader.uint32()));
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.duplicates.push(reader.string());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.idempotentId = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.correlationId = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SendMessageResponse {
    return {
      errors: globalThis.Array.isArray(object?.errors)
        ? object.errors.map((e: any) => SendMessageError.fromJSON(e))
        : [],
      duplicates: globalThis.Array.isArray(object?.duplicates)
        ? object.duplicates.map((e: any) => globalThis.String(e))
        : [],
      idempotentId: isSet(object.idempotentId) ? globalThis.String(object.idempotentId) : "",
      correlationId: isSet(object.correlationId) ? globalThis.String(object.correlationId) : "",
    };
  },

  toJSON(message: SendMessageResponse): unknown {
    const obj: any = {};
    if (message.errors?.length) {
      obj.errors = message.errors.map((e) => SendMessageError.toJSON(e));
    }
    if (message.duplicates?.length) {
      obj.duplicates = message.duplicates;
    }
    if (message.idempotentId !== "") {
      obj.idempotentId = message.idempotentId;
    }
    if (message.correlationId !== "") {
      obj.correlationId = message.correlationId;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SendMessageResponse>, I>>(base?: I): SendMessageResponse {
    return SendMessageResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SendMessageResponse>, I>>(object: I): SendMessageResponse {
    const message = createBaseSendMessageResponse();
    message.errors = object.errors?.map((e) => SendMessageError.fromPartial(e)) || [];
    message.duplicates = object.duplicates?.map((e) => e) || [];
    message.idempotentId = object.idempotentId ?? "";
    message.correlationId = object.correlationId ?? "";
    return message;
  },
};

function createBaseSendReceipt(): SendReceipt {
  return { request: undefined, response: undefined, serverEnvelope: undefined };
}

export const SendReceipt = {
  encode(message: SendReceipt, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.request !== undefined) {
      SendMessageRequest.encode(message.request, writer.uint32(10).fork()).ldelim();
    }
    if (message.response !== undefined) {
      SendMessageResponse.encode(message.response, writer.uint32(18).fork()).ldelim();
    }
    if (message.serverEnvelope !== undefined) {
      ServerEnvelope.encode(message.serverEnvelope, writer.uint32(26).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SendReceipt {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSendReceipt();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.request = SendMessageRequest.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.response = SendMessageResponse.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.serverEnvelope = ServerEnvelope.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SendReceipt {
    return {
      request: isSet(object.request) ? SendMessageRequest.fromJSON(object.request) : undefined,
      response: isSet(object.response) ? SendMessageResponse.fromJSON(object.response) : undefined,
      serverEnvelope: isSet(object.serverEnvelope) ? ServerEnvelope.fromJSON(object.serverEnvelope) : undefined,
    };
  },

  toJSON(message: SendReceipt): unknown {
    const obj: any = {};
    if (message.request !== undefined) {
      obj.request = SendMessageRequest.toJSON(message.request);
    }
    if (message.response !== undefined) {
      obj.response = SendMessageResponse.toJSON(message.response);
    }
    if (message.serverEnvelope !== undefined) {
      obj.serverEnvelope = ServerEnvelope.toJSON(message.serverEnvelope);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SendReceipt>, I>>(base?: I): SendReceipt {
    return SendReceipt.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SendReceipt>, I>>(object: I): SendReceipt {
    const message = createBaseSendReceipt();
    message.request = (object.request !== undefined && object.request !== null)
      ? SendMessageRequest.fromPartial(object.request)
      : undefined;
    message.response = (object.response !== undefined && object.response !== null)
      ? SendMessageResponse.fromPartial(object.response)
      : undefined;
    message.serverEnvelope = (object.serverEnvelope !== undefined && object.serverEnvelope !== null)
      ? ServerEnvelope.fromPartial(object.serverEnvelope)
      : undefined;
    return message;
  },
};

function createBaseSendMessageError(): SendMessageError {
  return { message: "", to: "" };
}

export const SendMessageError = {
  encode(message: SendMessageError, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.message !== "") {
      writer.uint32(10).string(message.message);
    }
    if (message.to !== "") {
      writer.uint32(18).string(message.to);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SendMessageError {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSendMessageError();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.message = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.to = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SendMessageError {
    return {
      message: isSet(object.message) ? globalThis.String(object.message) : "",
      to: isSet(object.to) ? globalThis.String(object.to) : "",
    };
  },

  toJSON(message: SendMessageError): unknown {
    const obj: any = {};
    if (message.message !== "") {
      obj.message = message.message;
    }
    if (message.to !== "") {
      obj.to = message.to;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SendMessageError>, I>>(base?: I): SendMessageError {
    return SendMessageError.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SendMessageError>, I>>(object: I): SendMessageError {
    const message = createBaseSendMessageError();
    message.message = object.message ?? "";
    message.to = object.to ?? "";
    return message;
  },
};

function createBaseFirstMessage(): FirstMessage {
  return { senderInfo: undefined, mailboxTimeoutInMs: 0, subscriptions: [], authToken: "", meta: undefined };
}

export const FirstMessage = {
  encode(message: FirstMessage, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.senderInfo !== undefined) {
      SenderInfo.encode(message.senderInfo, writer.uint32(10).fork()).ldelim();
    }
    if (message.mailboxTimeoutInMs !== 0) {
      writer.uint32(16).uint32(message.mailboxTimeoutInMs);
    }
    for (const v of message.subscriptions) {
      Subscription.encode(v!, writer.uint32(26).fork()).ldelim();
    }
    if (message.authToken !== "") {
      writer.uint32(34).string(message.authToken);
    }
    if (message.meta !== undefined) {
      Struct.encode(Struct.wrap(message.meta), writer.uint32(42).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): FirstMessage {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseFirstMessage();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.senderInfo = SenderInfo.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.mailboxTimeoutInMs = reader.uint32();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.subscriptions.push(Subscription.decode(reader, reader.uint32()));
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.authToken = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.meta = Struct.unwrap(Struct.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): FirstMessage {
    return {
      senderInfo: isSet(object.senderInfo) ? SenderInfo.fromJSON(object.senderInfo) : undefined,
      mailboxTimeoutInMs: isSet(object.mailboxTimeoutInMs) ? globalThis.Number(object.mailboxTimeoutInMs) : 0,
      subscriptions: globalThis.Array.isArray(object?.subscriptions)
        ? object.subscriptions.map((e: any) => Subscription.fromJSON(e))
        : [],
      authToken: isSet(object.authToken) ? globalThis.String(object.authToken) : "",
      meta: isObject(object.meta) ? object.meta : undefined,
    };
  },

  toJSON(message: FirstMessage): unknown {
    const obj: any = {};
    if (message.senderInfo !== undefined) {
      obj.senderInfo = SenderInfo.toJSON(message.senderInfo);
    }
    if (message.mailboxTimeoutInMs !== 0) {
      obj.mailboxTimeoutInMs = Math.round(message.mailboxTimeoutInMs);
    }
    if (message.subscriptions?.length) {
      obj.subscriptions = message.subscriptions.map((e) => Subscription.toJSON(e));
    }
    if (message.authToken !== "") {
      obj.authToken = message.authToken;
    }
    if (message.meta !== undefined) {
      obj.meta = message.meta;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<FirstMessage>, I>>(base?: I): FirstMessage {
    return FirstMessage.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<FirstMessage>, I>>(object: I): FirstMessage {
    const message = createBaseFirstMessage();
    message.senderInfo = (object.senderInfo !== undefined && object.senderInfo !== null)
      ? SenderInfo.fromPartial(object.senderInfo)
      : undefined;
    message.mailboxTimeoutInMs = object.mailboxTimeoutInMs ?? 0;
    message.subscriptions = object.subscriptions?.map((e) => Subscription.fromPartial(e)) || [];
    message.authToken = object.authToken ?? "";
    message.meta = object.meta ?? undefined;
    return message;
  },
};

function createBaseSenderInfo(): SenderInfo {
  return { readerKey: "", address: "" };
}

export const SenderInfo = {
  encode(message: SenderInfo, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.readerKey !== "") {
      writer.uint32(10).string(message.readerKey);
    }
    if (message.address !== "") {
      writer.uint32(18).string(message.address);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SenderInfo {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSenderInfo();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.readerKey = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.address = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SenderInfo {
    return {
      readerKey: isSet(object.readerKey) ? globalThis.String(object.readerKey) : "",
      address: isSet(object.address) ? globalThis.String(object.address) : "",
    };
  },

  toJSON(message: SenderInfo): unknown {
    const obj: any = {};
    if (message.readerKey !== "") {
      obj.readerKey = message.readerKey;
    }
    if (message.address !== "") {
      obj.address = message.address;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SenderInfo>, I>>(base?: I): SenderInfo {
    return SenderInfo.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SenderInfo>, I>>(object: I): SenderInfo {
    const message = createBaseSenderInfo();
    message.readerKey = object.readerKey ?? "";
    message.address = object.address ?? "";
    return message;
  },
};

function createBaseSubscribeRequest(): SubscribeRequest {
  return { subscriptions: [] };
}

export const SubscribeRequest = {
  encode(message: SubscribeRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    for (const v of message.subscriptions) {
      Subscription.encode(v!, writer.uint32(10).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SubscribeRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSubscribeRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.subscriptions.push(Subscription.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SubscribeRequest {
    return {
      subscriptions: globalThis.Array.isArray(object?.subscriptions)
        ? object.subscriptions.map((e: any) => Subscription.fromJSON(e))
        : [],
    };
  },

  toJSON(message: SubscribeRequest): unknown {
    const obj: any = {};
    if (message.subscriptions?.length) {
      obj.subscriptions = message.subscriptions.map((e) => Subscription.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SubscribeRequest>, I>>(base?: I): SubscribeRequest {
    return SubscribeRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SubscribeRequest>, I>>(object: I): SubscribeRequest {
    const message = createBaseSubscribeRequest();
    message.subscriptions = object.subscriptions?.map((e) => Subscription.fromPartial(e)) || [];
    return message;
  },
};

function createBaseSubscribeResponse(): SubscribeResponse {
  return { succeeded: [], errors: [] };
}

export const SubscribeResponse = {
  encode(message: SubscribeResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    for (const v of message.succeeded) {
      writer.uint32(10).string(v!);
    }
    for (const v of message.errors) {
      SubscribeError.encode(v!, writer.uint32(18).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SubscribeResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSubscribeResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.succeeded.push(reader.string());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.errors.push(SubscribeError.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SubscribeResponse {
    return {
      succeeded: globalThis.Array.isArray(object?.succeeded)
        ? object.succeeded.map((e: any) => globalThis.String(e))
        : [],
      errors: globalThis.Array.isArray(object?.errors) ? object.errors.map((e: any) => SubscribeError.fromJSON(e)) : [],
    };
  },

  toJSON(message: SubscribeResponse): unknown {
    const obj: any = {};
    if (message.succeeded?.length) {
      obj.succeeded = message.succeeded;
    }
    if (message.errors?.length) {
      obj.errors = message.errors.map((e) => SubscribeError.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SubscribeResponse>, I>>(base?: I): SubscribeResponse {
    return SubscribeResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SubscribeResponse>, I>>(object: I): SubscribeResponse {
    const message = createBaseSubscribeResponse();
    message.succeeded = object.succeeded?.map((e) => e) || [];
    message.errors = object.errors?.map((e) => SubscribeError.fromPartial(e)) || [];
    return message;
  },
};

function createBaseSubscribeError(): SubscribeError {
  return { id: "", state: "", message: "" };
}

export const SubscribeError = {
  encode(message: SubscribeError, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.state !== "") {
      writer.uint32(18).string(message.state);
    }
    if (message.message !== "") {
      writer.uint32(26).string(message.message);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SubscribeError {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSubscribeError();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.state = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.message = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SubscribeError {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      state: isSet(object.state) ? globalThis.String(object.state) : "",
      message: isSet(object.message) ? globalThis.String(object.message) : "",
    };
  },

  toJSON(message: SubscribeError): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.state !== "") {
      obj.state = message.state;
    }
    if (message.message !== "") {
      obj.message = message.message;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SubscribeError>, I>>(base?: I): SubscribeError {
    return SubscribeError.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SubscribeError>, I>>(object: I): SubscribeError {
    const message = createBaseSubscribeError();
    message.id = object.id ?? "";
    message.state = object.state ?? "";
    message.message = object.message ?? "";
    return message;
  },
};

function createBaseSubscription(): Subscription {
  return {
    mailbox: undefined,
    continuum: undefined,
    changeDataCapture: undefined,
    unsubscribe: undefined,
    subject: undefined,
  };
}

export const Subscription = {
  encode(message: Subscription, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.mailbox !== undefined) {
      MailboxSubscription.encode(message.mailbox, writer.uint32(10).fork()).ldelim();
    }
    if (message.continuum !== undefined) {
      ContinuumSubscription.encode(message.continuum, writer.uint32(18).fork()).ldelim();
    }
    if (message.changeDataCapture !== undefined) {
      ChangeDataCaptureSubscription.encode(message.changeDataCapture, writer.uint32(26).fork()).ldelim();
    }
    if (message.unsubscribe !== undefined) {
      Unsubscribe.encode(message.unsubscribe, writer.uint32(34).fork()).ldelim();
    }
    if (message.subject !== undefined) {
      SubjectSubscription.encode(message.subject, writer.uint32(42).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Subscription {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSubscription();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.mailbox = MailboxSubscription.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.continuum = ContinuumSubscription.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.changeDataCapture = ChangeDataCaptureSubscription.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.unsubscribe = Unsubscribe.decode(reader, reader.uint32());
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.subject = SubjectSubscription.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Subscription {
    return {
      mailbox: isSet(object.mailbox) ? MailboxSubscription.fromJSON(object.mailbox) : undefined,
      continuum: isSet(object.continuum) ? ContinuumSubscription.fromJSON(object.continuum) : undefined,
      changeDataCapture: isSet(object.changeDataCapture)
        ? ChangeDataCaptureSubscription.fromJSON(object.changeDataCapture)
        : undefined,
      unsubscribe: isSet(object.unsubscribe) ? Unsubscribe.fromJSON(object.unsubscribe) : undefined,
      subject: isSet(object.subject) ? SubjectSubscription.fromJSON(object.subject) : undefined,
    };
  },

  toJSON(message: Subscription): unknown {
    const obj: any = {};
    if (message.mailbox !== undefined) {
      obj.mailbox = MailboxSubscription.toJSON(message.mailbox);
    }
    if (message.continuum !== undefined) {
      obj.continuum = ContinuumSubscription.toJSON(message.continuum);
    }
    if (message.changeDataCapture !== undefined) {
      obj.changeDataCapture = ChangeDataCaptureSubscription.toJSON(message.changeDataCapture);
    }
    if (message.unsubscribe !== undefined) {
      obj.unsubscribe = Unsubscribe.toJSON(message.unsubscribe);
    }
    if (message.subject !== undefined) {
      obj.subject = SubjectSubscription.toJSON(message.subject);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Subscription>, I>>(base?: I): Subscription {
    return Subscription.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Subscription>, I>>(object: I): Subscription {
    const message = createBaseSubscription();
    message.mailbox = (object.mailbox !== undefined && object.mailbox !== null)
      ? MailboxSubscription.fromPartial(object.mailbox)
      : undefined;
    message.continuum = (object.continuum !== undefined && object.continuum !== null)
      ? ContinuumSubscription.fromPartial(object.continuum)
      : undefined;
    message.changeDataCapture = (object.changeDataCapture !== undefined && object.changeDataCapture !== null)
      ? ChangeDataCaptureSubscription.fromPartial(object.changeDataCapture)
      : undefined;
    message.unsubscribe = (object.unsubscribe !== undefined && object.unsubscribe !== null)
      ? Unsubscribe.fromPartial(object.unsubscribe)
      : undefined;
    message.subject = (object.subject !== undefined && object.subject !== null)
      ? SubjectSubscription.fromPartial(object.subject)
      : undefined;
    return message;
  },
};

function createBaseMailboxSubscription(): MailboxSubscription {
  return { id: "", state: "", readerKey: "", channel: "", startSeq: "", endSeq: "" };
}

export const MailboxSubscription = {
  encode(message: MailboxSubscription, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.state !== "") {
      writer.uint32(18).string(message.state);
    }
    if (message.readerKey !== "") {
      writer.uint32(26).string(message.readerKey);
    }
    if (message.channel !== "") {
      writer.uint32(34).string(message.channel);
    }
    if (message.startSeq !== "") {
      writer.uint32(42).string(message.startSeq);
    }
    if (message.endSeq !== "") {
      writer.uint32(50).string(message.endSeq);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MailboxSubscription {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMailboxSubscription();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.state = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.readerKey = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.channel = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.startSeq = reader.string();
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.endSeq = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): MailboxSubscription {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      state: isSet(object.state) ? globalThis.String(object.state) : "",
      readerKey: isSet(object.readerKey) ? globalThis.String(object.readerKey) : "",
      channel: isSet(object.channel) ? globalThis.String(object.channel) : "",
      startSeq: isSet(object.startSeq) ? globalThis.String(object.startSeq) : "",
      endSeq: isSet(object.endSeq) ? globalThis.String(object.endSeq) : "",
    };
  },

  toJSON(message: MailboxSubscription): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.state !== "") {
      obj.state = message.state;
    }
    if (message.readerKey !== "") {
      obj.readerKey = message.readerKey;
    }
    if (message.channel !== "") {
      obj.channel = message.channel;
    }
    if (message.startSeq !== "") {
      obj.startSeq = message.startSeq;
    }
    if (message.endSeq !== "") {
      obj.endSeq = message.endSeq;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<MailboxSubscription>, I>>(base?: I): MailboxSubscription {
    return MailboxSubscription.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<MailboxSubscription>, I>>(object: I): MailboxSubscription {
    const message = createBaseMailboxSubscription();
    message.id = object.id ?? "";
    message.state = object.state ?? "";
    message.readerKey = object.readerKey ?? "";
    message.channel = object.channel ?? "";
    message.startSeq = object.startSeq ?? "";
    message.endSeq = object.endSeq ?? "";
    return message;
  },
};

function createBaseChangeDataCaptureSubscription(): ChangeDataCaptureSubscription {
  return { id: "", state: "", matchers: [], startSeq: "", endSeq: "" };
}

export const ChangeDataCaptureSubscription = {
  encode(message: ChangeDataCaptureSubscription, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.state !== "") {
      writer.uint32(18).string(message.state);
    }
    for (const v of message.matchers) {
      RecordMatcher.encode(v!, writer.uint32(26).fork()).ldelim();
    }
    if (message.startSeq !== "") {
      writer.uint32(34).string(message.startSeq);
    }
    if (message.endSeq !== "") {
      writer.uint32(42).string(message.endSeq);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ChangeDataCaptureSubscription {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseChangeDataCaptureSubscription();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.state = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.matchers.push(RecordMatcher.decode(reader, reader.uint32()));
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.startSeq = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.endSeq = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ChangeDataCaptureSubscription {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      state: isSet(object.state) ? globalThis.String(object.state) : "",
      matchers: globalThis.Array.isArray(object?.matchers)
        ? object.matchers.map((e: any) => RecordMatcher.fromJSON(e))
        : [],
      startSeq: isSet(object.startSeq) ? globalThis.String(object.startSeq) : "",
      endSeq: isSet(object.endSeq) ? globalThis.String(object.endSeq) : "",
    };
  },

  toJSON(message: ChangeDataCaptureSubscription): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.state !== "") {
      obj.state = message.state;
    }
    if (message.matchers?.length) {
      obj.matchers = message.matchers.map((e) => RecordMatcher.toJSON(e));
    }
    if (message.startSeq !== "") {
      obj.startSeq = message.startSeq;
    }
    if (message.endSeq !== "") {
      obj.endSeq = message.endSeq;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ChangeDataCaptureSubscription>, I>>(base?: I): ChangeDataCaptureSubscription {
    return ChangeDataCaptureSubscription.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ChangeDataCaptureSubscription>, I>>(
    object: I,
  ): ChangeDataCaptureSubscription {
    const message = createBaseChangeDataCaptureSubscription();
    message.id = object.id ?? "";
    message.state = object.state ?? "";
    message.matchers = object.matchers?.map((e) => RecordMatcher.fromPartial(e)) || [];
    message.startSeq = object.startSeq ?? "";
    message.endSeq = object.endSeq ?? "";
    return message;
  },
};

function createBaseRecordMatcher(): RecordMatcher {
  return { database: "", table: "", primaryKeys: [] };
}

export const RecordMatcher = {
  encode(message: RecordMatcher, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.database !== "") {
      writer.uint32(10).string(message.database);
    }
    if (message.table !== "") {
      writer.uint32(18).string(message.table);
    }
    for (const v of message.primaryKeys) {
      Value.encode(Value.wrap(v!), writer.uint32(26).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): RecordMatcher {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseRecordMatcher();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.database = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.table = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.primaryKeys.push(Value.unwrap(Value.decode(reader, reader.uint32())));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): RecordMatcher {
    return {
      database: isSet(object.database) ? globalThis.String(object.database) : "",
      table: isSet(object.table) ? globalThis.String(object.table) : "",
      primaryKeys: globalThis.Array.isArray(object?.primaryKeys) ? [...object.primaryKeys] : [],
    };
  },

  toJSON(message: RecordMatcher): unknown {
    const obj: any = {};
    if (message.database !== "") {
      obj.database = message.database;
    }
    if (message.table !== "") {
      obj.table = message.table;
    }
    if (message.primaryKeys?.length) {
      obj.primaryKeys = message.primaryKeys;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<RecordMatcher>, I>>(base?: I): RecordMatcher {
    return RecordMatcher.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<RecordMatcher>, I>>(object: I): RecordMatcher {
    const message = createBaseRecordMatcher();
    message.database = object.database ?? "";
    message.table = object.table ?? "";
    message.primaryKeys = object.primaryKeys?.map((e) => e) || [];
    return message;
  },
};

function createBaseContinuumSubscription(): ContinuumSubscription {
  return { id: "", state: "", processUid: "", channel: "", startSeq: "", endSeq: "" };
}

export const ContinuumSubscription = {
  encode(message: ContinuumSubscription, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.state !== "") {
      writer.uint32(18).string(message.state);
    }
    if (message.processUid !== "") {
      writer.uint32(26).string(message.processUid);
    }
    if (message.channel !== "") {
      writer.uint32(34).string(message.channel);
    }
    if (message.startSeq !== "") {
      writer.uint32(42).string(message.startSeq);
    }
    if (message.endSeq !== "") {
      writer.uint32(50).string(message.endSeq);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ContinuumSubscription {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseContinuumSubscription();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.state = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.processUid = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.channel = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.startSeq = reader.string();
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.endSeq = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ContinuumSubscription {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      state: isSet(object.state) ? globalThis.String(object.state) : "",
      processUid: isSet(object.processUid) ? globalThis.String(object.processUid) : "",
      channel: isSet(object.channel) ? globalThis.String(object.channel) : "",
      startSeq: isSet(object.startSeq) ? globalThis.String(object.startSeq) : "",
      endSeq: isSet(object.endSeq) ? globalThis.String(object.endSeq) : "",
    };
  },

  toJSON(message: ContinuumSubscription): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.state !== "") {
      obj.state = message.state;
    }
    if (message.processUid !== "") {
      obj.processUid = message.processUid;
    }
    if (message.channel !== "") {
      obj.channel = message.channel;
    }
    if (message.startSeq !== "") {
      obj.startSeq = message.startSeq;
    }
    if (message.endSeq !== "") {
      obj.endSeq = message.endSeq;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ContinuumSubscription>, I>>(base?: I): ContinuumSubscription {
    return ContinuumSubscription.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ContinuumSubscription>, I>>(object: I): ContinuumSubscription {
    const message = createBaseContinuumSubscription();
    message.id = object.id ?? "";
    message.state = object.state ?? "";
    message.processUid = object.processUid ?? "";
    message.channel = object.channel ?? "";
    message.startSeq = object.startSeq ?? "";
    message.endSeq = object.endSeq ?? "";
    return message;
  },
};

function createBaseUnsubscribe(): Unsubscribe {
  return { id: "" };
}

export const Unsubscribe = {
  encode(message: Unsubscribe, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Unsubscribe {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUnsubscribe();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Unsubscribe {
    return { id: isSet(object.id) ? globalThis.String(object.id) : "" };
  },

  toJSON(message: Unsubscribe): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Unsubscribe>, I>>(base?: I): Unsubscribe {
    return Unsubscribe.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Unsubscribe>, I>>(object: I): Unsubscribe {
    const message = createBaseUnsubscribe();
    message.id = object.id ?? "";
    return message;
  },
};

function createBaseSubjectSubscription(): SubjectSubscription {
  return { id: "", state: "", subject: "", streamName: "", startSeq: "", endSeq: "" };
}

export const SubjectSubscription = {
  encode(message: SubjectSubscription, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.state !== "") {
      writer.uint32(18).string(message.state);
    }
    if (message.subject !== "") {
      writer.uint32(26).string(message.subject);
    }
    if (message.streamName !== "") {
      writer.uint32(34).string(message.streamName);
    }
    if (message.startSeq !== "") {
      writer.uint32(42).string(message.startSeq);
    }
    if (message.endSeq !== "") {
      writer.uint32(50).string(message.endSeq);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SubjectSubscription {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSubjectSubscription();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.state = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.subject = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.streamName = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.startSeq = reader.string();
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.endSeq = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SubjectSubscription {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      state: isSet(object.state) ? globalThis.String(object.state) : "",
      subject: isSet(object.subject) ? globalThis.String(object.subject) : "",
      streamName: isSet(object.streamName) ? globalThis.String(object.streamName) : "",
      startSeq: isSet(object.startSeq) ? globalThis.String(object.startSeq) : "",
      endSeq: isSet(object.endSeq) ? globalThis.String(object.endSeq) : "",
    };
  },

  toJSON(message: SubjectSubscription): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.state !== "") {
      obj.state = message.state;
    }
    if (message.subject !== "") {
      obj.subject = message.subject;
    }
    if (message.streamName !== "") {
      obj.streamName = message.streamName;
    }
    if (message.startSeq !== "") {
      obj.startSeq = message.startSeq;
    }
    if (message.endSeq !== "") {
      obj.endSeq = message.endSeq;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SubjectSubscription>, I>>(base?: I): SubjectSubscription {
    return SubjectSubscription.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SubjectSubscription>, I>>(object: I): SubjectSubscription {
    const message = createBaseSubjectSubscription();
    message.id = object.id ?? "";
    message.state = object.state ?? "";
    message.subject = object.subject ?? "";
    message.streamName = object.streamName ?? "";
    message.startSeq = object.startSeq ?? "";
    message.endSeq = object.endSeq ?? "";
    return message;
  },
};

function createBasePublishToSubject(): PublishToSubject {
  return { subject: "", payload: new Uint8Array(0), token: "" };
}

export const PublishToSubject = {
  encode(message: PublishToSubject, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.subject !== "") {
      writer.uint32(10).string(message.subject);
    }
    if (message.payload.length !== 0) {
      writer.uint32(18).bytes(message.payload);
    }
    if (message.token !== "") {
      writer.uint32(26).string(message.token);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PublishToSubject {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePublishToSubject();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.subject = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.payload = reader.bytes();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.token = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PublishToSubject {
    return {
      subject: isSet(object.subject) ? globalThis.String(object.subject) : "",
      payload: isSet(object.payload) ? bytesFromBase64(object.payload) : new Uint8Array(0),
      token: isSet(object.token) ? globalThis.String(object.token) : "",
    };
  },

  toJSON(message: PublishToSubject): unknown {
    const obj: any = {};
    if (message.subject !== "") {
      obj.subject = message.subject;
    }
    if (message.payload.length !== 0) {
      obj.payload = base64FromBytes(message.payload);
    }
    if (message.token !== "") {
      obj.token = message.token;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PublishToSubject>, I>>(base?: I): PublishToSubject {
    return PublishToSubject.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PublishToSubject>, I>>(object: I): PublishToSubject {
    const message = createBasePublishToSubject();
    message.subject = object.subject ?? "";
    message.payload = object.payload ?? new Uint8Array(0);
    message.token = object.token ?? "";
    return message;
  },
};

function createBaseCreateTopic(): CreateTopic {
  return { name: "", subjects: [] };
}

export const CreateTopic = {
  encode(message: CreateTopic, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.name !== "") {
      writer.uint32(10).string(message.name);
    }
    for (const v of message.subjects) {
      writer.uint32(18).string(v!);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): CreateTopic {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseCreateTopic();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.name = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.subjects.push(reader.string());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): CreateTopic {
    return {
      name: isSet(object.name) ? globalThis.String(object.name) : "",
      subjects: globalThis.Array.isArray(object?.subjects) ? object.subjects.map((e: any) => globalThis.String(e)) : [],
    };
  },

  toJSON(message: CreateTopic): unknown {
    const obj: any = {};
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.subjects?.length) {
      obj.subjects = message.subjects;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<CreateTopic>, I>>(base?: I): CreateTopic {
    return CreateTopic.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<CreateTopic>, I>>(object: I): CreateTopic {
    const message = createBaseCreateTopic();
    message.name = object.name ?? "";
    message.subjects = object.subjects?.map((e) => e) || [];
    return message;
  },
};

function createBaseDevLoopResponse(): DevLoopResponse {
  return { kind: "", token: "", ok: false, message: "" };
}

export const DevLoopResponse = {
  encode(message: DevLoopResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.kind !== "") {
      writer.uint32(10).string(message.kind);
    }
    if (message.token !== "") {
      writer.uint32(18).string(message.token);
    }
    if (message.ok === true) {
      writer.uint32(24).bool(message.ok);
    }
    if (message.message !== "") {
      writer.uint32(34).string(message.message);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): DevLoopResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseDevLoopResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.kind = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.token = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.ok = reader.bool();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.message = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): DevLoopResponse {
    return {
      kind: isSet(object.kind) ? globalThis.String(object.kind) : "",
      token: isSet(object.token) ? globalThis.String(object.token) : "",
      ok: isSet(object.ok) ? globalThis.Boolean(object.ok) : false,
      message: isSet(object.message) ? globalThis.String(object.message) : "",
    };
  },

  toJSON(message: DevLoopResponse): unknown {
    const obj: any = {};
    if (message.kind !== "") {
      obj.kind = message.kind;
    }
    if (message.token !== "") {
      obj.token = message.token;
    }
    if (message.ok === true) {
      obj.ok = message.ok;
    }
    if (message.message !== "") {
      obj.message = message.message;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<DevLoopResponse>, I>>(base?: I): DevLoopResponse {
    return DevLoopResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<DevLoopResponse>, I>>(object: I): DevLoopResponse {
    const message = createBaseDevLoopResponse();
    message.kind = object.kind ?? "";
    message.token = object.token ?? "";
    message.ok = object.ok ?? false;
    message.message = object.message ?? "";
    return message;
  },
};

function createBaseCreateMailboxRequest(): CreateMailboxRequest {
  return {
    channels: [],
    privateMetadata: undefined,
    publicMetadata: undefined,
    purgeTimeoutInMillis: "0",
    closeTimeoutInMillis: "0",
    extraData: undefined,
    lifecycleKind: "",
    processRunUid: "",
  };
}

export const CreateMailboxRequest = {
  encode(message: CreateMailboxRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    for (const v of message.channels) {
      writer.uint32(10).string(v!);
    }
    if (message.privateMetadata !== undefined) {
      Struct.encode(Struct.wrap(message.privateMetadata), writer.uint32(18).fork()).ldelim();
    }
    if (message.publicMetadata !== undefined) {
      Struct.encode(Struct.wrap(message.publicMetadata), writer.uint32(26).fork()).ldelim();
    }
    if (message.purgeTimeoutInMillis !== "0") {
      writer.uint32(32).int64(message.purgeTimeoutInMillis);
    }
    if (message.closeTimeoutInMillis !== "0") {
      writer.uint32(40).int64(message.closeTimeoutInMillis);
    }
    if (message.extraData !== undefined) {
      Struct.encode(Struct.wrap(message.extraData), writer.uint32(50).fork()).ldelim();
    }
    if (message.lifecycleKind !== "") {
      writer.uint32(58).string(message.lifecycleKind);
    }
    if (message.processRunUid !== "") {
      writer.uint32(66).string(message.processRunUid);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): CreateMailboxRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseCreateMailboxRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.channels.push(reader.string());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.privateMetadata = Struct.unwrap(Struct.decode(reader, reader.uint32()));
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.publicMetadata = Struct.unwrap(Struct.decode(reader, reader.uint32()));
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.purgeTimeoutInMillis = longToString(reader.int64() as Long);
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.closeTimeoutInMillis = longToString(reader.int64() as Long);
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.extraData = Struct.unwrap(Struct.decode(reader, reader.uint32()));
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.lifecycleKind = reader.string();
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.processRunUid = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): CreateMailboxRequest {
    return {
      channels: globalThis.Array.isArray(object?.channels) ? object.channels.map((e: any) => globalThis.String(e)) : [],
      privateMetadata: isObject(object.privateMetadata) ? object.privateMetadata : undefined,
      publicMetadata: isObject(object.publicMetadata) ? object.publicMetadata : undefined,
      purgeTimeoutInMillis: isSet(object.purgeTimeoutInMillis) ? globalThis.String(object.purgeTimeoutInMillis) : "0",
      closeTimeoutInMillis: isSet(object.closeTimeoutInMillis) ? globalThis.String(object.closeTimeoutInMillis) : "0",
      extraData: isObject(object.extraData) ? object.extraData : undefined,
      lifecycleKind: isSet(object.lifecycleKind) ? globalThis.String(object.lifecycleKind) : "",
      processRunUid: isSet(object.processRunUid) ? globalThis.String(object.processRunUid) : "",
    };
  },

  toJSON(message: CreateMailboxRequest): unknown {
    const obj: any = {};
    if (message.channels?.length) {
      obj.channels = message.channels;
    }
    if (message.privateMetadata !== undefined) {
      obj.privateMetadata = message.privateMetadata;
    }
    if (message.publicMetadata !== undefined) {
      obj.publicMetadata = message.publicMetadata;
    }
    if (message.purgeTimeoutInMillis !== "0") {
      obj.purgeTimeoutInMillis = message.purgeTimeoutInMillis;
    }
    if (message.closeTimeoutInMillis !== "0") {
      obj.closeTimeoutInMillis = message.closeTimeoutInMillis;
    }
    if (message.extraData !== undefined) {
      obj.extraData = message.extraData;
    }
    if (message.lifecycleKind !== "") {
      obj.lifecycleKind = message.lifecycleKind;
    }
    if (message.processRunUid !== "") {
      obj.processRunUid = message.processRunUid;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<CreateMailboxRequest>, I>>(base?: I): CreateMailboxRequest {
    return CreateMailboxRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<CreateMailboxRequest>, I>>(object: I): CreateMailboxRequest {
    const message = createBaseCreateMailboxRequest();
    message.channels = object.channels?.map((e) => e) || [];
    message.privateMetadata = object.privateMetadata ?? undefined;
    message.publicMetadata = object.publicMetadata ?? undefined;
    message.purgeTimeoutInMillis = object.purgeTimeoutInMillis ?? "0";
    message.closeTimeoutInMillis = object.closeTimeoutInMillis ?? "0";
    message.extraData = object.extraData ?? undefined;
    message.lifecycleKind = object.lifecycleKind ?? "";
    message.processRunUid = object.processRunUid ?? "";
    return message;
  },
};

function createBaseCreateMailboxResponse(): CreateMailboxResponse {
  return { adminKey: "", address: "", readerKey: "", channels: [] };
}

export const CreateMailboxResponse = {
  encode(message: CreateMailboxResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.adminKey !== "") {
      writer.uint32(10).string(message.adminKey);
    }
    if (message.address !== "") {
      writer.uint32(18).string(message.address);
    }
    if (message.readerKey !== "") {
      writer.uint32(26).string(message.readerKey);
    }
    for (const v of message.channels) {
      writer.uint32(34).string(v!);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): CreateMailboxResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseCreateMailboxResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.adminKey = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.address = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.readerKey = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.channels.push(reader.string());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): CreateMailboxResponse {
    return {
      adminKey: isSet(object.adminKey) ? globalThis.String(object.adminKey) : "",
      address: isSet(object.address) ? globalThis.String(object.address) : "",
      readerKey: isSet(object.readerKey) ? globalThis.String(object.readerKey) : "",
      channels: globalThis.Array.isArray(object?.channels) ? object.channels.map((e: any) => globalThis.String(e)) : [],
    };
  },

  toJSON(message: CreateMailboxResponse): unknown {
    const obj: any = {};
    if (message.adminKey !== "") {
      obj.adminKey = message.adminKey;
    }
    if (message.address !== "") {
      obj.address = message.address;
    }
    if (message.readerKey !== "") {
      obj.readerKey = message.readerKey;
    }
    if (message.channels?.length) {
      obj.channels = message.channels;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<CreateMailboxResponse>, I>>(base?: I): CreateMailboxResponse {
    return CreateMailboxResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<CreateMailboxResponse>, I>>(object: I): CreateMailboxResponse {
    const message = createBaseCreateMailboxResponse();
    message.adminKey = object.adminKey ?? "";
    message.address = object.address ?? "";
    message.readerKey = object.readerKey ?? "";
    message.channels = object.channels?.map((e) => e) || [];
    return message;
  },
};

function createBaseAddChannelRequest(): AddChannelRequest {
  return { adminKey: "", channels: [] };
}

export const AddChannelRequest = {
  encode(message: AddChannelRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.adminKey !== "") {
      writer.uint32(10).string(message.adminKey);
    }
    for (const v of message.channels) {
      writer.uint32(18).string(v!);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): AddChannelRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseAddChannelRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.adminKey = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.channels.push(reader.string());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): AddChannelRequest {
    return {
      adminKey: isSet(object.adminKey) ? globalThis.String(object.adminKey) : "",
      channels: globalThis.Array.isArray(object?.channels) ? object.channels.map((e: any) => globalThis.String(e)) : [],
    };
  },

  toJSON(message: AddChannelRequest): unknown {
    const obj: any = {};
    if (message.adminKey !== "") {
      obj.adminKey = message.adminKey;
    }
    if (message.channels?.length) {
      obj.channels = message.channels;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<AddChannelRequest>, I>>(base?: I): AddChannelRequest {
    return AddChannelRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<AddChannelRequest>, I>>(object: I): AddChannelRequest {
    const message = createBaseAddChannelRequest();
    message.adminKey = object.adminKey ?? "";
    message.channels = object.channels?.map((e) => e) || [];
    return message;
  },
};

function createBaseAddChannelResponse(): AddChannelResponse {
  return {};
}

export const AddChannelResponse = {
  encode(_: AddChannelResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): AddChannelResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseAddChannelResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(_: any): AddChannelResponse {
    return {};
  },

  toJSON(_: AddChannelResponse): unknown {
    const obj: any = {};
    return obj;
  },

  create<I extends Exact<DeepPartial<AddChannelResponse>, I>>(base?: I): AddChannelResponse {
    return AddChannelResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<AddChannelResponse>, I>>(_: I): AddChannelResponse {
    const message = createBaseAddChannelResponse();
    return message;
  },
};

export interface MeshService {
  SendReceive(request: Observable<MessageFromClient>): Observable<MessageToClient>;
  CreateMailbox(request: CreateMailboxRequest): Promise<CreateMailboxResponse>;
  AddChannel(request: AddChannelRequest): Promise<AddChannelResponse>;
}

export const MeshServiceServiceName = "mesh.MeshService";
export class MeshServiceClientImpl implements MeshService {
  private readonly rpc: Rpc;
  private readonly service: string;
  constructor(rpc: Rpc, opts?: { service?: string }) {
    this.service = opts?.service || MeshServiceServiceName;
    this.rpc = rpc;
    this.SendReceive = this.SendReceive.bind(this);
    this.CreateMailbox = this.CreateMailbox.bind(this);
    this.AddChannel = this.AddChannel.bind(this);
  }
  SendReceive(request: Observable<MessageFromClient>): Observable<MessageToClient> {
    const data = request.pipe(map((request) => MessageFromClient.encode(request).finish()));
    const result = this.rpc.bidirectionalStreamingRequest(this.service, "SendReceive", data);
    return result.pipe(map((data) => MessageToClient.decode(_m0.Reader.create(data))));
  }

  CreateMailbox(request: CreateMailboxRequest): Promise<CreateMailboxResponse> {
    const data = CreateMailboxRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "CreateMailbox", data);
    return promise.then((data) => CreateMailboxResponse.decode(_m0.Reader.create(data)));
  }

  AddChannel(request: AddChannelRequest): Promise<AddChannelResponse> {
    const data = AddChannelRequest.encode(request).finish();
    const promise = this.rpc.request(this.service, "AddChannel", data);
    return promise.then((data) => AddChannelResponse.decode(_m0.Reader.create(data)));
  }
}

interface Rpc {
  request(service: string, method: string, data: Uint8Array): Promise<Uint8Array>;
  clientStreamingRequest(service: string, method: string, data: Observable<Uint8Array>): Promise<Uint8Array>;
  serverStreamingRequest(service: string, method: string, data: Uint8Array): Observable<Uint8Array>;
  bidirectionalStreamingRequest(service: string, method: string, data: Observable<Uint8Array>): Observable<Uint8Array>;
}

function bytesFromBase64(b64: string): Uint8Array {
  if (globalThis.Buffer) {
    return Uint8Array.from(globalThis.Buffer.from(b64, "base64"));
  } else {
    const bin = globalThis.atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; ++i) {
      arr[i] = bin.charCodeAt(i);
    }
    return arr;
  }
}

function base64FromBytes(arr: Uint8Array): string {
  if (globalThis.Buffer) {
    return globalThis.Buffer.from(arr).toString("base64");
  } else {
    const bin: string[] = [];
    arr.forEach((byte) => {
      bin.push(globalThis.String.fromCharCode(byte));
    });
    return globalThis.btoa(bin.join(""));
  }
}

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function toTimestamp(date: Date): Timestamp {
  const seconds = Math.trunc(date.getTime() / 1_000).toString();
  const nanos = (date.getTime() % 1_000) * 1_000_000;
  return { seconds, nanos };
}

function fromTimestamp(t: Timestamp): Date {
  let millis = (globalThis.Number(t.seconds) || 0) * 1_000;
  millis += (t.nanos || 0) / 1_000_000;
  return new globalThis.Date(millis);
}

function fromJsonTimestamp(o: any): Date {
  if (o instanceof globalThis.Date) {
    return o;
  } else if (typeof o === "string") {
    return new globalThis.Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}

function longToString(long: Long) {
  return long.toString();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isObject(value: any): boolean {
  return typeof value === "object" && value !== null;
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
